// Settings › Usage (D74 credit plans): the shared credit balance, spend by
// service (Chat, Agents, Apps) and by person, and per-person monthly limits.
//
// Namespace `usage`; owner: ui/usage. Plans have no names: a plan is the
// credits it adds every month. Never show what a credit costs Poneva.
// `{credits}` / `{used}` / `{limit}` / `{notice}` / `{balance}` / `{count}` are
// formatted numbers, `{percent}` a formatted percentage with its sign ("20%",
// "20 %"), `{date}` / `{from}` / `{to}` / `{day}` formatted dates, `{time}` a
// formatted time and `{name}` a person.
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
// `peopleNoun` lists CLDR plural forms for `formatCount` (../format.ts). Where a
// language's noun agrees with the number, a sentence puts the number after a
// colon or in parentheses instead of inflecting it.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  eyebrow: 'Company usage',
  title: 'Usage',
  intro: 'Your company’s shared credit balance, and how your team spends it.',
  introPersonal: 'Your company’s shared credit balance, and how you spend it.',
  refresh: 'Refresh',
  updated: 'Updated {time}',
  updatedNow: 'Updated just now',
  from: 'From',
  to: 'To',
  chooseFirstDay: 'Choose the first day',
  chooseLastDay: 'Choose the last day',
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  thisPeriod: 'This period',
  person: 'Person',
  allUsers: 'All users',
  peopleNoun: 'one:person|other:people',
  personMenu: 'Show spend for one person',
  loading: 'Loading usage…',
  unavailable: 'Usage is not available right now.',
  forbidden: 'You don’t have access to this company’s usage.',
  tryAgain: 'Try again',
  balanceTitle: 'Credit balance',
  creditsUnit: 'credits',
  paused: 'Usage paused',
  planPerMonth: '{credits} credits / month',
  planRenews: '+{credits} credits on {date}',
  planSwitches: 'Switches to {credits} credits / month on {date}',
  planEnds: 'Plan ends on {date}',
  noPlan: 'No plan yet',
  planPaymentFailed: 'Payment failed',
  planEndedOn: 'Ended on {date}',
  planEnded: 'Ended',
  openBilling: 'Go to Billing',
  summaryTopShareSelf: '{percent} of your credits',
  discountUntil: '{percent} off until {date}',
  discountOngoing: '{percent} off',
  neverExpire: 'Credits never expire',
  changePlan: 'Change plan',
  upgradeNow: 'Upgrade now',
  choosePlan: 'Choose a plan',
  manageOnWeb: 'Plans are managed in Poneva on the web.',
  noticePlanOwner:
    'Usage is paused because the company has no active plan. Choose a plan to start using credits again.',
  noticePlanMember:
    'Usage is paused because the company has no active plan. Ask your company owner to choose one.',
  noticeOutOwner:
    'You’ve used all your credits. Upgrade now, or wait until {date} at {time} when your plan adds {credits} credits.',
  noticeOutMember:
    'Your company has used all its credits. Ask your company owner to upgrade, or wait until {date} at {time} when the plan adds {credits} credits.',
  noticeOutNoRenewal: 'You’ve used all your credits. Choose a plan to add more.',
  noticeLimit:
    'You’ve reached your monthly limit of {limit} credits. You can use credits again on {date}, or sooner if your company owner raises your limit.',
  noticeLimitSoon:
    'You’ve used {used} of your {limit} monthly credits. You’re paused when you reach {limit}.',
  unpriced: '{count} actions are not priced yet and are left out of these numbers.',
  balanceAria:
    'Credit balance from {from} to {to}. {balance} credits now. Use the arrow keys to read each day.',
  chartAria: 'Daily credits from {from} to {to}. Use the arrow keys to read each day.',
  flagRenewed: 'Renewed {date}',
  flagNewPlan: 'New plan {date}',
  flagAdded: 'Added {date}',
  nextRenewal: 'on {date}',
  nextNewPlan: 'new plan from {date}',
  today: 'Today, {time}',
  soFar: '{day}, so far',
  spent: '{credits} spent',
  spentSoFar: '{credits} spent so far',
  fromNewPlan: '+{credits} from the new plan',
  atRenewal: '+{credits} at renewal',
  added: '+{credits} added',
  balanceValue: '{credits} credits',
  creditsInTotal: 'credits in total',
  outOfCredits: 'Out of credits',
  serviceTitle: 'Credit spend by service',
  serviceSub: 'Daily credits for the whole company',
  serviceSubPersonal: 'Your daily credits',
  chat: 'Chat',
  agents: 'Agents',
  apps: 'Apps',
  service: 'Service',
  credits: 'Credits',
  share: 'Share',
  avgPerDay: 'Avg per day',
  total: 'Total',
  empty: 'No credits used in this period',
  emptyBody: 'Spend shows here as your team uses Chat, Agents and Apps.',
  userTitle: 'Credit spend by user',
  userSub: 'Daily credits for the top {count} people and everyone else',
  userSubOne: 'Daily credits by service for {name}',
  everyoneElse: 'Everyone else ({count})',
  member: 'Member',
  usedLimit: 'Used / monthly limit',
  limitColumn: 'Limit',
  selectedColumn: 'Selected',
  you: 'You',
  owner: 'Owner',
  admin: 'Admin',
  memberRole: 'Member',
  pausedTag: 'Paused',
  usedNoLimit: '{used} used · No limit',
  noLimit: 'No limit',
  ofLimit: '/ {limit}',
  setLimit: 'Set limit',
  editLimit: 'Edit limit',
  showSpendFor: 'Show spend for {name}',
  selectedShowAll: '{name}, selected. Show everyone again',
  limitFor: 'Monthly limit for {name}',
  creditsPerMonth: 'credits / month',
  limitHelp: 'Choose how many credits {name} can use each month.',
  limitInvalid: 'Enter a whole number of credits, 1 or more.',
  limitPausesNow:
    '{name} has already used {used} this period, so this limit pauses them right away.',
  limitHelpValid:
    '{name} has used {used} this period. They get a notice at {notice} and are paused at {limit}.',
  save: 'Save',
  saving: 'Saving…',
  removeLimit: 'Remove limit',
  cancel: 'Cancel',
  limitSaved: 'The monthly limit for {name} is {limit} credits.',
  limitRemoved: '{name} has no monthly limit now.',
  limitFailed: 'We couldn’t save the limit. Try again.',
  noteLimits:
    'A member who reaches their limit is paused until the next plan renewal or until the owner raises it. They get a notice at 80%.',
  noteAdmin:
    'You see everyone’s spend because you’re an owner or admin. Members only see their own.',
  notePersonal: 'You see your own spend. Owners and admins see everyone’s.',
  summaryCredits: 'Credits',
  summaryShare: 'Share of company spend',
  summaryTop: 'Uses most',
  summaryTopShare: '{percent} of their credits',
  summaryRoleYou: '{role} · You',
  showAllUsers: 'Show all users',
  planChanged: 'Your plan is updated.',
};

export type UsageCopyKey = keyof typeof en;
type Copy = Record<UsageCopyKey, string>;

const sv: Copy = {
  eyebrow: 'Företagets användning',
  title: 'Användning',
  intro: 'Företagets gemensamma kreditsaldo och hur ditt team använder det.',
  introPersonal: 'Företagets gemensamma kreditsaldo och hur du använder det.',
  refresh: 'Uppdatera',
  updated: 'Uppdaterad kl. {time}',
  updatedNow: 'Uppdaterad nyss',
  from: 'Från',
  to: 'Till',
  chooseFirstDay: 'Välj första dagen',
  chooseLastDay: 'Välj sista dagen',
  previousMonth: 'Föregående månad',
  nextMonth: 'Nästa månad',
  last7: 'Senaste 7 dagarna',
  last30: 'Senaste 30 dagarna',
  last90: 'Senaste 90 dagarna',
  thisPeriod: 'Nuvarande period',
  person: 'Person',
  allUsers: 'Alla användare',
  peopleNoun: 'one:person|other:personer',
  personMenu: 'Visa förbrukning för en person',
  loading: 'Läser in användningen…',
  unavailable: 'Användningen är inte tillgänglig just nu.',
  forbidden: 'Du har inte åtkomst till företagets användning.',
  tryAgain: 'Försök igen',
  balanceTitle: 'Kreditsaldo',
  creditsUnit: 'krediter',
  paused: 'Användning pausad',
  planPerMonth: '{credits} krediter/månad',
  planRenews: '+{credits} krediter den {date}',
  planSwitches: 'Byter till {credits} krediter/månad den {date}',
  planEnds: 'Planen slutar den {date}',
  noPlan: 'Ingen plan än',
  planPaymentFailed: 'Betalningen misslyckades',
  planEndedOn: 'Slutade den {date}',
  planEnded: 'Avslutad',
  openBilling: 'Gå till Fakturering',
  summaryTopShareSelf: '{percent} av dina krediter',
  discountUntil: '{percent} rabatt till {date}',
  discountOngoing: '{percent} rabatt',
  neverExpire: 'Krediterna förfaller aldrig',
  changePlan: 'Byt plan',
  upgradeNow: 'Uppgradera nu',
  choosePlan: 'Välj en plan',
  manageOnWeb: 'Planer hanteras i Poneva på webben.',
  noticePlanOwner:
    'Användningen är pausad eftersom företaget inte har någon aktiv plan. Välj en plan för att börja använda krediter igen.',
  noticePlanMember:
    'Användningen är pausad eftersom företaget inte har någon aktiv plan. Be företagets ägare att välja en.',
  noticeOutOwner:
    'Du har använt alla krediter. Uppgradera nu, eller vänta till {date} kl. {time} när planen lägger till {credits} krediter.',
  noticeOutMember:
    'Företaget har använt alla sina krediter. Be företagets ägare att uppgradera, eller vänta till {date} kl. {time} när planen lägger till {credits} krediter.',
  noticeOutNoRenewal: 'Du har använt alla krediter. Välj en plan för att få fler.',
  noticeLimit:
    'Du har nått din månadsgräns på {limit} krediter. Du kan använda krediter igen den {date}, eller tidigare om företagets ägare höjer din gräns.',
  noticeLimitSoon:
    'Du har använt {used} av dina {limit} krediter den här månaden. Användningen pausas när du når {limit}.',
  unpriced: '{count} åtgärder har inget pris än och ingår inte i siffrorna.',
  balanceAria:
    'Kreditsaldo från {from} till {to}. Saldot är nu {balance} krediter. Använd piltangenterna för att gå igenom dag för dag.',
  chartAria:
    'Krediter per dag från {from} till {to}. Använd piltangenterna för att gå igenom dag för dag.',
  flagRenewed: 'Förnyad {date}',
  flagNewPlan: 'Ny plan {date}',
  flagAdded: 'Tillagt {date}',
  nextRenewal: 'den {date}',
  nextNewPlan: 'ny plan från {date}',
  today: 'I dag kl. {time}',
  soFar: '{day}, hittills',
  spent: '{credits} förbrukade',
  spentSoFar: '{credits} förbrukade hittills',
  fromNewPlan: '+{credits} från den nya planen',
  atRenewal: '+{credits} vid förnyelsen',
  added: '+{credits} tillagda',
  balanceValue: '{credits} krediter',
  creditsInTotal: 'krediter totalt',
  outOfCredits: 'Slut på krediter',
  serviceTitle: 'Kreditförbrukning per tjänst',
  serviceSub: 'Krediter per dag för hela företaget',
  serviceSubPersonal: 'Dina krediter per dag',
  chat: 'Chatt',
  agents: 'Agenter',
  apps: 'Appar',
  service: 'Tjänst',
  credits: 'Krediter',
  share: 'Andel',
  avgPerDay: 'Snitt per dag',
  total: 'Totalt',
  empty: 'Inga krediter har använts under perioden',
  emptyBody: 'Förbrukningen visas här när ditt team använder Chatt, Agenter och Appar.',
  userTitle: 'Kreditförbrukning per användare',
  userSub: 'Krediter per dag för de {count} som använder mest och för alla andra',
  userSubOne: 'Krediter per dag och tjänst för {name}',
  everyoneElse: 'Alla andra ({count})',
  member: 'Medlem',
  usedLimit: 'Använt / månadsgräns',
  limitColumn: 'Gräns',
  selectedColumn: 'Vald',
  you: 'Du',
  owner: 'Ägare',
  admin: 'Administratör',
  memberRole: 'Medlem',
  pausedTag: 'Pausad',
  usedNoLimit: 'Använt: {used} · Ingen gräns',
  noLimit: 'Ingen gräns',
  ofLimit: '/ {limit}',
  setLimit: 'Ange gräns',
  editLimit: 'Ändra gräns',
  showSpendFor: 'Visa förbrukning för {name}',
  selectedShowAll: '{name}, vald. Visa alla igen',
  limitFor: 'Månadsgräns för {name}',
  creditsPerMonth: 'krediter/månad',
  limitHelp: 'Välj hur många krediter {name} får använda varje månad.',
  limitInvalid: 'Ange ett helt antal krediter, minst 1.',
  limitPausesNow:
    '{name} har redan använt {used} den här perioden, så gränsen pausar personen direkt.',
  limitHelpValid:
    '{name} har använt {used} den här perioden. Personen får ett meddelande vid {notice} och pausas vid {limit}.',
  save: 'Spara',
  saving: 'Sparar…',
  removeLimit: 'Ta bort gränsen',
  cancel: 'Avbryt',
  limitSaved: 'Månadsgränsen för {name} är {limit} krediter.',
  limitRemoved: '{name} har ingen månadsgräns längre.',
  limitFailed: 'Vi kunde inte spara gränsen. Försök igen.',
  noteLimits:
    'En medlem som når sin gräns pausas till nästa förnyelse av planen eller tills ägaren höjer gränsen. Medlemmen får ett meddelande vid 80 %.',
  noteAdmin:
    'Du ser allas förbrukning eftersom du är ägare eller administratör. Medlemmar ser bara sin egen.',
  notePersonal: 'Du ser din egen förbrukning. Ägare och administratörer ser allas.',
  summaryCredits: 'Krediter',
  summaryShare: 'Andel av företagets förbrukning',
  summaryTop: 'Använder mest',
  summaryTopShare: '{percent} av personens krediter',
  summaryRoleYou: '{role} · Du',
  showAllUsers: 'Visa alla användare',
  planChanged: 'Din plan är uppdaterad.',
};

const bg: Copy = {
  eyebrow: 'Използване във фирмата',
  title: 'Използване',
  intro: 'Общият кредитен баланс на вашата фирма и как екипът ви го използва.',
  introPersonal: 'Общият кредитен баланс на вашата фирма и как вие го използвате.',
  refresh: 'Опресняване',
  updated: 'Обновено в {time}',
  updatedNow: 'Обновено току-що',
  from: 'От',
  to: 'До',
  chooseFirstDay: 'Изберете първия ден',
  chooseLastDay: 'Изберете последния ден',
  previousMonth: 'Предишен месец',
  nextMonth: 'Следващ месец',
  last7: 'Последните 7 дни',
  last30: 'Последните 30 дни',
  last90: 'Последните 90 дни',
  thisPeriod: 'Текущ период',
  person: 'Човек',
  allUsers: 'Всички потребители',
  peopleNoun: 'one:човек|other:души',
  personMenu: 'Показване на потреблението на един човек',
  loading: 'Използването се зарежда…',
  unavailable: 'Използването не е налично в момента.',
  forbidden: 'Нямате достъп до използването на тази фирма.',
  tryAgain: 'Опитайте отново',
  balanceTitle: 'Кредитен баланс',
  creditsUnit: 'кредита',
  paused: 'Използването е спряно',
  planPerMonth: '{credits} кредита/месец',
  planRenews: '+{credits} кредита на {date}',
  planSwitches: 'Сменя се на {credits} кредита/месец на {date}',
  planEnds: 'Планът приключва на {date}',
  noPlan: 'Все още няма план',
  planPaymentFailed: 'Плащането е неуспешно',
  planEndedOn: 'Приключи на {date}',
  planEnded: 'Приключил',
  openBilling: 'Към Плащания',
  summaryTopShareSelf: '{percent} от вашите кредити',
  discountUntil: '{percent} отстъпка до {date}',
  discountOngoing: '{percent} отстъпка',
  neverExpire: 'Кредитите никога не изтичат',
  changePlan: 'Смяна на плана',
  upgradeNow: 'Надградете сега',
  choosePlan: 'Изберете план',
  manageOnWeb: 'Плановете се управляват в уеб версията на Poneva.',
  noticePlanOwner:
    'Използването е спряно, защото фирмата няма активен план. Изберете план, за да използвате кредити отново.',
  noticePlanMember:
    'Използването е спряно, защото фирмата няма активен план. Помолете собственика на фирмата да избере такъв.',
  noticeOutOwner:
    'Използвахте всичките си кредити. Надградете сега или изчакайте до {date} в {time}, когато планът ви ще добави {credits} кредита.',
  noticeOutMember:
    'Фирмата ви използва всичките си кредити. Помолете собственика на фирмата да надгради плана или изчакайте до {date} в {time}, когато планът ще добави {credits} кредита.',
  noticeOutNoRenewal: 'Използвахте всичките си кредити. Изберете план, за да получите още.',
  noticeLimit:
    'Достигнахте месечния си лимит от {limit} кредита. Ще можете да използвате кредити отново на {date} или по-рано, ако собственикът на фирмата увеличи лимита ви.',
  noticeLimitSoon:
    'Използвахте {used} от вашите {limit} месечни кредита. Използването ще спре, когато достигнете {limit}.',
  unpriced: '{count} действия все още нямат цена и не са включени в тези числа.',
  balanceAria:
    'Кредитен баланс от {from} до {to}. В момента {balance} кредита. Използвайте клавишите със стрелки, за да прочетете всеки ден.',
  chartAria:
    'Кредити на ден от {from} до {to}. Използвайте клавишите със стрелки, за да прочетете всеки ден.',
  flagRenewed: 'Подновен на {date}',
  flagNewPlan: 'Нов план на {date}',
  flagAdded: 'Добавени на {date}',
  nextRenewal: 'на {date}',
  nextNewPlan: 'нов план от {date}',
  today: 'Днес, {time}',
  soFar: '{day}, досега',
  spent: '{credits} изразходвани',
  spentSoFar: '{credits} изразходвани досега',
  fromNewPlan: '+{credits} от новия план',
  atRenewal: '+{credits} при подновяване',
  added: '+{credits} добавени',
  balanceValue: '{credits} кредита',
  creditsInTotal: 'кредита общо',
  outOfCredits: 'Няма кредити',
  serviceTitle: 'Изразходвани кредити по услуга',
  serviceSub: 'Кредити на ден за цялата фирма',
  serviceSubPersonal: 'Вашите кредити на ден',
  chat: 'Чат',
  agents: 'Агенти',
  apps: 'Приложения',
  service: 'Услуга',
  credits: 'Кредити',
  share: 'Дял',
  avgPerDay: 'Средно на ден',
  total: 'Общо',
  empty: 'Няма използвани кредити през този период',
  emptyBody: 'Потреблението ще се показва тук, когато екипът ви използва Чат, Агенти и Приложения.',
  userTitle: 'Изразходвани кредити по потребител',
  userSub: 'Кредити на ден за най-активните ({count}) и всички останали',
  userSubOne: 'Кредити на ден по услуга за {name}',
  everyoneElse: 'Всички останали ({count})',
  member: 'Член',
  usedLimit: 'Използвани / месечен лимит',
  limitColumn: 'Лимит',
  selectedColumn: 'Избран',
  you: 'Вие',
  owner: 'Собственик',
  admin: 'Администратор',
  memberRole: 'Член',
  pausedTag: 'Спрян',
  usedNoLimit: '{used} използвани · Без лимит',
  noLimit: 'Без лимит',
  ofLimit: '/ {limit}',
  setLimit: 'Задаване на лимит',
  editLimit: 'Редактиране на лимита',
  showSpendFor: 'Показване на потреблението на {name}',
  selectedShowAll: 'Избран: {name}. Показване на всички отново',
  limitFor: 'Месечен лимит за {name}',
  creditsPerMonth: 'кредита/месец',
  limitHelp: 'Изберете колко кредита може да използва {name} всеки месец.',
  limitInvalid: 'Въведете цяло число кредити, 1 или повече.',
  limitPausesNow:
    'Потреблението на {name} за този период вече е {used}, затова този лимит спира използването веднага.',
  limitHelpValid:
    'Потреблението на {name} за този период е {used}. Известие идва при {notice}, а използването спира при {limit}.',
  save: 'Запазване',
  saving: 'Запазва се…',
  removeLimit: 'Премахване на лимита',
  cancel: 'Отказ',
  limitSaved: 'Месечният лимит за {name} е {limit} кредита.',
  limitRemoved: '{name} вече няма месечен лимит.',
  limitFailed: 'Лимитът не можа да бъде запазен. Опитайте отново.',
  noteLimits:
    'Член, който достигне лимита си, се спира до следващото подновяване на плана или докато собственикът не увеличи лимита. При 80% получава известие.',
  noteAdmin:
    'Виждате потреблението на всички, защото сте собственик или администратор. Членовете виждат само своето.',
  notePersonal:
    'Виждате собственото си потребление. Собствениците и администраторите виждат това на всички.',
  summaryCredits: 'Кредити',
  summaryShare: 'Дял от потреблението на фирмата',
  summaryTop: 'Използва най-много',
  summaryTopShare: '{percent} от собствените кредити',
  summaryRoleYou: '{role} · Вие',
  showAllUsers: 'Показване на всички потребители',
  planChanged: 'Планът ви е обновен.',
};

const hr: Copy = {
  eyebrow: 'Potrošnja tvrtke',
  title: 'Potrošnja',
  intro: 'Zajednički saldo kredita vaše tvrtke i kako ga troši vaš tim.',
  introPersonal: 'Zajednički saldo kredita vaše tvrtke i kako ga trošite vi.',
  refresh: 'Osvježi',
  updated: 'Ažurirano u {time}',
  updatedNow: 'Upravo ažurirano',
  from: 'Od',
  to: 'Do',
  chooseFirstDay: 'Odaberite prvi dan',
  chooseLastDay: 'Odaberite zadnji dan',
  previousMonth: 'Prethodni mjesec',
  nextMonth: 'Sljedeći mjesec',
  last7: 'Zadnjih 7 dana',
  last30: 'Zadnjih 30 dana',
  last90: 'Zadnjih 90 dana',
  thisPeriod: 'Trenutačno razdoblje',
  person: 'Osoba',
  allUsers: 'Svi korisnici',
  peopleNoun: 'one:osoba|few:osobe|other:osoba',
  personMenu: 'Prikaži potrošnju jedne osobe',
  loading: 'Učitavanje potrošnje…',
  unavailable: 'Potrošnja trenutačno nije dostupna.',
  forbidden: 'Nemate pristup potrošnji ove tvrtke.',
  tryAgain: 'Pokušaj ponovno',
  balanceTitle: 'Saldo kredita',
  creditsUnit: 'kredita',
  paused: 'Korištenje pauzirano',
  planPerMonth: '{credits} kredita mjesečno',
  planRenews: '+{credits} kredita {date}',
  planSwitches: 'Od {date}: {credits} kredita mjesečno',
  planEnds: 'Paket završava {date}',
  noPlan: 'Još nema paketa',
  planPaymentFailed: 'Plaćanje nije uspjelo',
  planEndedOn: 'Završio {date}',
  planEnded: 'Završen',
  openBilling: 'Idi na Naplatu',
  summaryTopShareSelf: '{percent} vaših kredita',
  discountUntil: '{percent} popusta do {date}',
  discountOngoing: '{percent} popusta',
  neverExpire: 'Krediti nikad ne istječu',
  changePlan: 'Promijeni paket',
  upgradeNow: 'Nadogradi sada',
  choosePlan: 'Odaberi paket',
  manageOnWeb: 'Paketima se upravlja u web-verziji aplikacije Poneva.',
  noticePlanOwner:
    'Korištenje je pauzirano jer tvrtka nema aktivan paket. Odaberite paket kako biste ponovno mogli koristiti kredite.',
  noticePlanMember:
    'Korištenje je pauzirano jer tvrtka nema aktivan paket. Zamolite vlasnika tvrtke da ga odabere.',
  noticeOutOwner:
    'Potrošili ste sve kredite. Nadogradite sada ili pričekajte do {date} u {time}, kada paket dodaje {credits} kredita.',
  noticeOutMember:
    'Vaša tvrtka potrošila je sve kredite. Zamolite vlasnika tvrtke za nadogradnju ili pričekajte do {date} u {time}, kada paket dodaje {credits} kredita.',
  noticeOutNoRenewal: 'Potrošili ste sve kredite. Odaberite paket kako biste dobili još.',
  noticeLimit:
    'Dosegli ste mjesečno ograničenje od {limit} kredita. Kredite ćete ponovno moći koristiti {date} ili ranije ako vlasnik tvrtke poveća vaše ograničenje.',
  noticeLimitSoon:
    'Potrošili ste {used} od {limit} mjesečnih kredita. Korištenje se pauzira kada dosegnete {limit}.',
  unpriced: 'Broj radnji koje još nemaju cijenu i nisu uključene u ove brojke: {count}.',
  balanceAria:
    'Saldo kredita od {from} do {to}. Trenutačno {balance} kredita. Tipkama sa strelicama pročitajte pojedine dane.',
  chartAria: 'Dnevni krediti od {from} do {to}. Tipkama sa strelicama pročitajte pojedine dane.',
  flagRenewed: 'Obnovljeno {date}',
  flagNewPlan: 'Novi paket {date}',
  flagAdded: 'Dodano {date}',
  nextRenewal: 'obnova {date}',
  nextNewPlan: 'novi paket od {date}',
  today: 'Danas, {time}',
  soFar: '{day}, zasad',
  spent: 'Potrošeno {credits}',
  spentSoFar: 'Zasad potrošeno {credits}',
  fromNewPlan: '+{credits} iz novog paketa',
  atRenewal: '+{credits} pri obnovi',
  added: 'Dodano +{credits}',
  balanceValue: '{credits} kredita',
  creditsInTotal: 'kredita ukupno',
  outOfCredits: 'Nema više kredita',
  serviceTitle: 'Potrošnja kredita po uslugama',
  serviceSub: 'Dnevni krediti cijele tvrtke',
  serviceSubPersonal: 'Vaši dnevni krediti',
  chat: 'Chat',
  agents: 'Agenti',
  apps: 'Aplikacije',
  service: 'Usluga',
  credits: 'Krediti',
  share: 'Udio',
  avgPerDay: 'Prosj. dnevno',
  total: 'Ukupno',
  empty: 'U ovom razdoblju nisu potrošeni krediti',
  emptyBody: 'Potrošnja se prikazuje ovdje kada vaš tim koristi Chat, Agente i Aplikacije.',
  userTitle: 'Potrošnja kredita po korisnicima',
  userSub: 'Dnevni krediti najaktivnijih korisnika ({count}) i svih ostalih',
  userSubOne: 'Dnevni krediti korisnika {name} po uslugama',
  everyoneElse: 'Svi ostali ({count})',
  member: 'Član',
  usedLimit: 'Potrošeno / mjesečno ograničenje',
  limitColumn: 'Ograničenje',
  selectedColumn: 'Odabrano',
  you: 'Vi',
  owner: 'Vlasnik',
  admin: 'Administrator',
  memberRole: 'Član',
  pausedTag: 'Pauzirano',
  usedNoLimit: 'Potrošeno {used} · Bez ograničenja',
  noLimit: 'Bez ograničenja',
  ofLimit: '/ {limit}',
  setLimit: 'Postavi ograničenje',
  editLimit: 'Uredi ograničenje',
  showSpendFor: 'Prikaži potrošnju korisnika {name}',
  selectedShowAll: 'Odabrano: {name}. Ponovno prikaži sve',
  limitFor: 'Mjesečno ograničenje korisnika {name}',
  creditsPerMonth: 'kredita mjesečno',
  limitHelp: 'Odaberite koliko kredita {name} može potrošiti svaki mjesec.',
  limitInvalid: 'Unesite cijeli broj kredita, najmanje 1.',
  limitPausesNow:
    'Potrošnja korisnika {name} u ovom razdoblju već je {used}, pa ovo ograničenje odmah pauzira korištenje.',
  limitHelpValid:
    'Potrošnja korisnika {name} u ovom razdoblju je {used}. Obavijest stiže pri {notice}, a korištenje se pauzira pri {limit}.',
  save: 'Spremi',
  saving: 'Spremanje…',
  removeLimit: 'Ukloni ograničenje',
  cancel: 'Odustani',
  limitSaved: 'Mjesečno ograničenje korisnika {name} je {limit} kredita.',
  limitRemoved: 'Korisnik {name} više nema mjesečno ograničenje.',
  limitFailed: 'Ograničenje nije spremljeno. Pokušajte ponovno.',
  noteLimits:
    'Član koji dosegne svoje ograničenje pauziran je do sljedeće obnove paketa ili dok vlasnik ne poveća ograničenje. Pri 80 % dobiva obavijest.',
  noteAdmin: 'Vidite potrošnju svih jer ste vlasnik ili administrator. Članovi vide samo svoju.',
  notePersonal: 'Vidite svoju potrošnju. Vlasnici i administratori vide potrošnju svih.',
  summaryCredits: 'Krediti',
  summaryShare: 'Udio u potrošnji tvrtke',
  summaryTop: 'Najviše koristi',
  summaryTopShare: '{percent} vlastitih kredita',
  summaryRoleYou: '{role} · Vi',
  showAllUsers: 'Prikaži sve korisnike',
  planChanged: 'Vaš paket je ažuriran.',
};

const cs: Copy = {
  eyebrow: 'Využití ve firmě',
  title: 'Využití',
  intro: 'Sdílený zůstatek kreditů vaší firmy a jak ho využívá váš tým.',
  introPersonal: 'Sdílený zůstatek kreditů vaší firmy a jak ho využíváte vy.',
  refresh: 'Obnovit',
  updated: 'Aktualizováno v {time}',
  updatedNow: 'Právě aktualizováno',
  from: 'Od',
  to: 'Do',
  chooseFirstDay: 'Vyberte první den',
  chooseLastDay: 'Vyberte poslední den',
  previousMonth: 'Předchozí měsíc',
  nextMonth: 'Další měsíc',
  last7: 'Posledních 7 dní',
  last30: 'Posledních 30 dní',
  last90: 'Posledních 90 dní',
  thisPeriod: 'Aktuální období',
  person: 'Osoba',
  allUsers: 'Všichni uživatelé',
  peopleNoun: 'one:osoba|few:osoby|many:osoby|other:osob',
  personMenu: 'Zobrazit spotřebu jedné osoby',
  loading: 'Načítání využití…',
  unavailable: 'Využití teď není k dispozici.',
  forbidden: 'Nemáte přístup k využití této firmy.',
  tryAgain: 'Zkusit znovu',
  balanceTitle: 'Zůstatek kreditů',
  creditsUnit: 'kreditů',
  paused: 'Využívání pozastaveno',
  planPerMonth: '{credits} kreditů měsíčně',
  planRenews: '+{credits} kreditů {date}',
  planSwitches: 'Od {date}: {credits} kreditů měsíčně',
  planEnds: 'Plán končí {date}',
  noPlan: 'Zatím bez plánu',
  planPaymentFailed: 'Platba se nezdařila',
  planEndedOn: 'Skončil {date}',
  planEnded: 'Skončil',
  openBilling: 'Přejít na Fakturaci',
  summaryTopShareSelf: '{percent} vašich kreditů',
  discountUntil: 'Sleva {percent} do {date}',
  discountOngoing: 'Sleva {percent}',
  neverExpire: 'Kredity nikdy nevyprší',
  changePlan: 'Změnit plán',
  upgradeNow: 'Přejít na vyšší plán',
  choosePlan: 'Vybrat plán',
  manageOnWeb: 'Plány se spravují ve webové verzi Poneva.',
  noticePlanOwner:
    'Využívání je pozastaveno, protože firma nemá aktivní plán. Vyberte plán, abyste mohli znovu používat kredity.',
  noticePlanMember:
    'Využívání je pozastaveno, protože firma nemá aktivní plán. Požádejte vlastníka firmy, aby nějaký vybral.',
  noticeOutOwner:
    'Vyčerpali jste všechny kredity. Přejděte na vyšší plán, nebo počkejte do {date} v {time}, kdy plán přidá {credits} kreditů.',
  noticeOutMember:
    'Vaše firma vyčerpala všechny kredity. Požádejte vlastníka firmy o vyšší plán, nebo počkejte do {date} v {time}, kdy plán přidá {credits} kreditů.',
  noticeOutNoRenewal: 'Vyčerpali jste všechny kredity. Vyberte plán a získejte další.',
  noticeLimit:
    'Dosáhli jste měsíčního limitu ({limit} kreditů). Kredity budete moci znovu používat {date}, nebo dříve, pokud vám vlastník firmy limit zvýší.',
  noticeLimitSoon:
    'Využili jste {used} z {limit} měsíčních kreditů. Při dosažení {limit} bude využívání pozastaveno.',
  unpriced: 'Počet akcí, které zatím nemají cenu a nejsou v těchto číslech zahrnuty: {count}.',
  balanceAria:
    'Zůstatek kreditů od {from} do {to}. Aktuální zůstatek: {balance}. Šipkami můžete procházet jednotlivé dny.',
  chartAria: 'Kredity po dnech od {from} do {to}. Šipkami můžete procházet jednotlivé dny.',
  flagRenewed: 'Obnoveno {date}',
  flagNewPlan: 'Nový plán {date}',
  flagAdded: 'Přidáno {date}',
  nextRenewal: 'obnovení {date}',
  nextNewPlan: 'nový plán od {date}',
  today: 'Dnes, {time}',
  soFar: '{day}, zatím',
  spent: 'Spotřebováno {credits}',
  spentSoFar: 'Zatím spotřebováno {credits}',
  fromNewPlan: '+{credits} z nového plánu',
  atRenewal: '+{credits} při obnovení',
  added: 'Přidáno +{credits}',
  balanceValue: 'Zůstatek: {credits}',
  creditsInTotal: 'celkem',
  outOfCredits: 'Kredity vyčerpány',
  serviceTitle: 'Spotřeba kreditů podle služby',
  serviceSub: 'Denní kredity celé firmy',
  serviceSubPersonal: 'Vaše denní kredity',
  chat: 'Chat',
  agents: 'Agenti',
  apps: 'Aplikace',
  service: 'Služba',
  credits: 'Kredity',
  share: 'Podíl',
  avgPerDay: 'Prům. za den',
  total: 'Celkem',
  empty: 'V tomto období nebyly použity žádné kredity',
  emptyBody: 'Spotřeba se tu zobrazí, jakmile váš tým začne používat Chat, Agenty a Aplikace.',
  userTitle: 'Spotřeba kreditů podle uživatele',
  userSub: 'Denní kredity nejaktivnějších uživatelů ({count}) a všech ostatních',
  userSubOne: 'Denní kredity uživatele {name} podle služby',
  everyoneElse: 'Všichni ostatní ({count})',
  member: 'Člen',
  usedLimit: 'Využito / měsíční limit',
  limitColumn: 'Limit',
  selectedColumn: 'Vybráno',
  you: 'Vy',
  owner: 'Vlastník',
  admin: 'Správce',
  memberRole: 'Člen',
  pausedTag: 'Pozastaveno',
  usedNoLimit: 'Využito {used} · Bez limitu',
  noLimit: 'Bez limitu',
  ofLimit: '/ {limit}',
  setLimit: 'Nastavit limit',
  editLimit: 'Upravit limit',
  showSpendFor: 'Zobrazit spotřebu uživatele {name}',
  selectedShowAll: 'Vybrán uživatel {name}. Znovu zobrazit všechny',
  limitFor: 'Měsíční limit uživatele {name}',
  creditsPerMonth: 'kreditů měsíčně',
  limitHelp: 'Zvolte, kolik kreditů může {name} každý měsíc využít.',
  limitInvalid: 'Zadejte celé číslo, alespoň 1.',
  limitPausesNow:
    'Uživatel {name} má v tomto období využito již {used}, takže tento limit využívání okamžitě pozastaví.',
  limitHelpValid:
    'Uživatel {name} má v tomto období využito {used}. Při {notice} přijde upozornění a při {limit} se využívání pozastaví.',
  save: 'Uložit',
  saving: 'Ukládání…',
  removeLimit: 'Odebrat limit',
  cancel: 'Zrušit',
  limitSaved: 'Měsíční limit uživatele {name}: {limit}.',
  limitRemoved: 'Uživatel {name} už nemá měsíční limit.',
  limitFailed: 'Limit se nepodařilo uložit. Zkuste to znovu.',
  noteLimits:
    'Člen, který dosáhne svého limitu, je pozastaven do příštího obnovení plánu nebo do doby, než mu vlastník limit zvýší. Při 80 % dostane upozornění.',
  noteAdmin:
    'Vidíte spotřebu všech, protože jste vlastník nebo správce. Členové vidí jen svou vlastní.',
  notePersonal: 'Vidíte svou vlastní spotřebu. Vlastníci a správci vidí spotřebu všech.',
  summaryCredits: 'Kredity',
  summaryShare: 'Podíl na spotřebě firmy',
  summaryTop: 'Nejvíc využívá',
  summaryTopShare: '{percent} vlastních kreditů',
  summaryRoleYou: '{role} · Vy',
  showAllUsers: 'Zobrazit všechny uživatele',
  planChanged: 'Váš plán byl aktualizován.',
};

const da: Copy = {
  eyebrow: 'Virksomhedens forbrug',
  title: 'Forbrug',
  intro: 'Virksomhedens fælles kreditsaldo, og hvordan dit team bruger den.',
  introPersonal: 'Virksomhedens fælles kreditsaldo, og hvordan du bruger den.',
  refresh: 'Opdater',
  updated: 'Opdateret kl. {time}',
  updatedNow: 'Opdateret lige nu',
  from: 'Fra',
  to: 'Til',
  chooseFirstDay: 'Vælg første dag',
  chooseLastDay: 'Vælg sidste dag',
  previousMonth: 'Forrige måned',
  nextMonth: 'Næste måned',
  last7: 'Seneste 7 dage',
  last30: 'Seneste 30 dage',
  last90: 'Seneste 90 dage',
  thisPeriod: 'Nuværende periode',
  person: 'Person',
  allUsers: 'Alle brugere',
  peopleNoun: 'one:person|other:personer',
  personMenu: 'Vis forbrug for én person',
  loading: 'Indlæser forbrug…',
  unavailable: 'Forbruget er ikke tilgængeligt lige nu.',
  forbidden: 'Du har ikke adgang til virksomhedens forbrug.',
  tryAgain: 'Prøv igen',
  balanceTitle: 'Kreditsaldo',
  creditsUnit: 'kreditter',
  paused: 'Forbrug sat på pause',
  planPerMonth: '{credits} kreditter/måned',
  planRenews: '+{credits} kreditter den {date}',
  planSwitches: 'Skifter til {credits} kreditter/måned den {date}',
  planEnds: 'Planen slutter den {date}',
  noPlan: 'Ingen plan endnu',
  planPaymentFailed: 'Betalingen mislykkedes',
  planEndedOn: 'Sluttede den {date}',
  planEnded: 'Afsluttet',
  openBilling: 'Gå til Fakturering',
  summaryTopShareSelf: '{percent} af dine kreditter',
  discountUntil: '{percent} rabat indtil {date}',
  discountOngoing: '{percent} rabat',
  neverExpire: 'Kreditter udløber aldrig',
  changePlan: 'Skift plan',
  upgradeNow: 'Opgrader nu',
  choosePlan: 'Vælg en plan',
  manageOnWeb: 'Planer administreres i Poneva på nettet.',
  noticePlanOwner:
    'Forbruget er sat på pause, fordi virksomheden ikke har en aktiv plan. Vælg en plan for at bruge kreditter igen.',
  noticePlanMember:
    'Forbruget er sat på pause, fordi virksomheden ikke har en aktiv plan. Bed virksomhedens ejer om at vælge en.',
  noticeOutOwner:
    'Du har brugt alle dine kreditter. Opgrader nu, eller vent til {date} kl. {time}, hvor din plan tilføjer {credits} kreditter.',
  noticeOutMember:
    'Virksomheden har brugt alle sine kreditter. Bed virksomhedens ejer om at opgradere, eller vent til {date} kl. {time}, hvor planen tilføjer {credits} kreditter.',
  noticeOutNoRenewal: 'Du har brugt alle dine kreditter. Vælg en plan for at få flere.',
  noticeLimit:
    'Du har nået din månedlige grænse på {limit} kreditter. Du kan bruge kreditter igen den {date}, eller før, hvis virksomhedens ejer hæver din grænse.',
  noticeLimitSoon:
    'Du har brugt {used} af dine {limit} månedlige kreditter. Forbruget sættes på pause, når du når {limit}.',
  unpriced: '{count} handlinger har endnu ingen pris og er ikke med i disse tal.',
  balanceAria:
    'Kreditsaldo fra {from} til {to}. Saldoen er nu {balance} kreditter. Brug piletasterne til at gå dag for dag.',
  chartAria: 'Kreditter pr. dag fra {from} til {to}. Brug piletasterne til at gå dag for dag.',
  flagRenewed: 'Fornyet {date}',
  flagNewPlan: 'Ny plan {date}',
  flagAdded: 'Tilføjet {date}',
  nextRenewal: 'den {date}',
  nextNewPlan: 'ny plan fra {date}',
  today: 'I dag kl. {time}',
  soFar: '{day}, indtil videre',
  spent: '{credits} brugt',
  spentSoFar: '{credits} brugt indtil videre',
  fromNewPlan: '+{credits} fra den nye plan',
  atRenewal: '+{credits} ved fornyelse',
  added: '+{credits} tilføjet',
  balanceValue: '{credits} kreditter',
  creditsInTotal: 'kreditter i alt',
  outOfCredits: 'Ingen kreditter tilbage',
  serviceTitle: 'Kreditforbrug pr. tjeneste',
  serviceSub: 'Kreditter pr. dag for hele virksomheden',
  serviceSubPersonal: 'Dine kreditter pr. dag',
  chat: 'Chat',
  agents: 'Agenter',
  apps: 'Apps',
  service: 'Tjeneste',
  credits: 'Kreditter',
  share: 'Andel',
  avgPerDay: 'Gns. pr. dag',
  total: 'I alt',
  empty: 'Ingen kreditter brugt i denne periode',
  emptyBody: 'Forbruget vises her, når dit team bruger Chat, Agenter og Apps.',
  userTitle: 'Kreditforbrug pr. bruger',
  userSub: 'Kreditter pr. dag for de {count}, der bruger mest, og alle andre',
  userSubOne: 'Kreditter pr. dag og tjeneste for {name}',
  everyoneElse: 'Alle andre ({count})',
  member: 'Medlem',
  usedLimit: 'Brugt / månedlig grænse',
  limitColumn: 'Grænse',
  selectedColumn: 'Valgt',
  you: 'Dig',
  owner: 'Ejer',
  admin: 'Administrator',
  memberRole: 'Medlem',
  pausedTag: 'På pause',
  usedNoLimit: '{used} brugt · Ingen grænse',
  noLimit: 'Ingen grænse',
  ofLimit: '/ {limit}',
  setLimit: 'Angiv grænse',
  editLimit: 'Rediger grænse',
  showSpendFor: 'Vis forbrug for {name}',
  selectedShowAll: '{name}, valgt. Vis alle igen',
  limitFor: 'Månedlig grænse for {name}',
  creditsPerMonth: 'kreditter/måned',
  limitHelp: 'Vælg, hvor mange kreditter {name} kan bruge hver måned.',
  limitInvalid: 'Angiv et helt antal kreditter, mindst 1.',
  limitPausesNow:
    '{name} har allerede brugt {used} i denne periode, så grænsen sætter personen på pause med det samme.',
  limitHelpValid:
    '{name} har brugt {used} i denne periode. Personen får besked ved {notice} og sættes på pause ved {limit}.',
  save: 'Gem',
  saving: 'Gemmer…',
  removeLimit: 'Fjern grænse',
  cancel: 'Annuller',
  limitSaved: 'Den månedlige grænse for {name} er {limit} kreditter.',
  limitRemoved: '{name} har ikke længere en månedlig grænse.',
  limitFailed: 'Vi kunne ikke gemme grænsen. Prøv igen.',
  noteLimits:
    'Et medlem, der når sin grænse, sættes på pause indtil næste fornyelse af planen, eller indtil ejeren hæver grænsen. Medlemmet får besked ved 80 %.',
  noteAdmin:
    'Du ser alles forbrug, fordi du er ejer eller administrator. Medlemmer ser kun deres eget.',
  notePersonal: 'Du ser dit eget forbrug. Ejere og administratorer ser alles.',
  summaryCredits: 'Kreditter',
  summaryShare: 'Andel af virksomhedens forbrug',
  summaryTop: 'Bruger mest',
  summaryTopShare: '{percent} af personens kreditter',
  summaryRoleYou: '{role} · Dig',
  showAllUsers: 'Vis alle brugere',
  planChanged: 'Din plan er opdateret.',
};

const nl: Copy = {
  eyebrow: 'Gebruik van het bedrijf',
  title: 'Gebruik',
  intro: 'Het gedeelde creditsaldo van je bedrijf en hoe je team het gebruikt.',
  introPersonal: 'Het gedeelde creditsaldo van je bedrijf en hoe jij het gebruikt.',
  refresh: 'Vernieuwen',
  updated: 'Bijgewerkt om {time}',
  updatedNow: 'Zojuist bijgewerkt',
  from: 'Van',
  to: 'Tot',
  chooseFirstDay: 'Kies de eerste dag',
  chooseLastDay: 'Kies de laatste dag',
  previousMonth: 'Vorige maand',
  nextMonth: 'Volgende maand',
  last7: 'Afgelopen 7 dagen',
  last30: 'Afgelopen 30 dagen',
  last90: 'Afgelopen 90 dagen',
  thisPeriod: 'Huidige periode',
  person: 'Persoon',
  allUsers: 'Alle gebruikers',
  peopleNoun: 'one:persoon|other:personen',
  personMenu: 'Verbruik van één persoon tonen',
  loading: 'Gebruik laden…',
  unavailable: 'Het gebruik is nu niet beschikbaar.',
  forbidden: 'Je hebt geen toegang tot het gebruik van dit bedrijf.',
  tryAgain: 'Opnieuw proberen',
  balanceTitle: 'Creditsaldo',
  creditsUnit: 'credits',
  paused: 'Gebruik gepauzeerd',
  planPerMonth: '{credits} credits/maand',
  planRenews: '+{credits} credits op {date}',
  planSwitches: 'Wisselt op {date} naar {credits} credits/maand',
  planEnds: 'Plan eindigt op {date}',
  noPlan: 'Nog geen plan',
  planPaymentFailed: 'Betaling mislukt',
  planEndedOn: 'Geëindigd op {date}',
  planEnded: 'Beëindigd',
  openBilling: 'Naar Facturering',
  summaryTopShareSelf: '{percent} van je credits',
  discountUntil: '{percent} korting tot {date}',
  discountOngoing: '{percent} korting',
  neverExpire: 'Credits verlopen nooit',
  changePlan: 'Plan wijzigen',
  upgradeNow: 'Nu upgraden',
  choosePlan: 'Kies een plan',
  manageOnWeb: 'Plannen beheer je in Poneva op het web.',
  noticePlanOwner:
    'Het gebruik is gepauzeerd omdat het bedrijf geen actief plan heeft. Kies een plan om weer credits te gebruiken.',
  noticePlanMember:
    'Het gebruik is gepauzeerd omdat het bedrijf geen actief plan heeft. Vraag de eigenaar van je bedrijf om er een te kiezen.',
  noticeOutOwner:
    'Je hebt al je credits gebruikt. Upgrade nu, of wacht tot {date} om {time}, wanneer je plan {credits} credits toevoegt.',
  noticeOutMember:
    'Je bedrijf heeft al zijn credits gebruikt. Vraag de eigenaar van je bedrijf om te upgraden, of wacht tot {date} om {time}, wanneer het plan {credits} credits toevoegt.',
  noticeOutNoRenewal: 'Je hebt al je credits gebruikt. Kies een plan om er meer te krijgen.',
  noticeLimit:
    'Je hebt je maandlimiet van {limit} credits bereikt. Je kunt op {date} weer credits gebruiken, of eerder als de eigenaar van je bedrijf je limiet verhoogt.',
  noticeLimitSoon:
    'Je hebt {used} van je {limit} maandelijkse credits gebruikt. Bij {limit} wordt je gebruik gepauzeerd.',
  unpriced: '{count} acties hebben nog geen prijs en tellen niet mee in deze cijfers.',
  balanceAria:
    'Creditsaldo van {from} tot {to}. Nu {balance} credits. Gebruik de pijltoetsen om dag voor dag te lezen.',
  chartAria:
    'Credits per dag van {from} tot {to}. Gebruik de pijltoetsen om dag voor dag te lezen.',
  flagRenewed: 'Verlengd {date}',
  flagNewPlan: 'Nieuw plan {date}',
  flagAdded: 'Toegevoegd {date}',
  nextRenewal: 'op {date}',
  nextNewPlan: 'nieuw plan vanaf {date}',
  today: 'Vandaag, {time}',
  soFar: '{day}, tot nu toe',
  spent: '{credits} verbruikt',
  spentSoFar: '{credits} verbruikt tot nu toe',
  fromNewPlan: '+{credits} uit het nieuwe plan',
  atRenewal: '+{credits} bij verlenging',
  added: '+{credits} toegevoegd',
  balanceValue: '{credits} credits',
  creditsInTotal: 'credits in totaal',
  outOfCredits: 'Geen credits meer',
  serviceTitle: 'Creditverbruik per dienst',
  serviceSub: 'Credits per dag voor het hele bedrijf',
  serviceSubPersonal: 'Jouw credits per dag',
  chat: 'Chat',
  agents: 'Agents',
  apps: 'Apps',
  service: 'Dienst',
  credits: 'Credits',
  share: 'Aandeel',
  avgPerDay: 'Gem. per dag',
  total: 'Totaal',
  empty: 'Geen credits gebruikt in deze periode',
  emptyBody: 'Het verbruik verschijnt hier zodra je team Chat, Agents en Apps gebruikt.',
  userTitle: 'Creditverbruik per gebruiker',
  userSub: 'Credits per dag voor de top {count} en alle anderen',
  userSubOne: 'Credits per dag en per dienst voor {name}',
  everyoneElse: 'Alle anderen ({count})',
  member: 'Lid',
  usedLimit: 'Gebruikt / maandlimiet',
  limitColumn: 'Limiet',
  selectedColumn: 'Geselecteerd',
  you: 'Jij',
  owner: 'Eigenaar',
  admin: 'Beheerder',
  memberRole: 'Lid',
  pausedTag: 'Gepauzeerd',
  usedNoLimit: '{used} gebruikt · Geen limiet',
  noLimit: 'Geen limiet',
  ofLimit: '/ {limit}',
  setLimit: 'Limiet instellen',
  editLimit: 'Limiet wijzigen',
  showSpendFor: 'Verbruik van {name} tonen',
  selectedShowAll: '{name}, geselecteerd. Iedereen weer tonen',
  limitFor: 'Maandlimiet voor {name}',
  creditsPerMonth: 'credits/maand',
  limitHelp: 'Kies hoeveel credits {name} elke maand mag gebruiken.',
  limitInvalid: 'Voer een heel aantal credits in, minimaal 1.',
  limitPausesNow:
    '{name} heeft deze periode al {used} gebruikt, dus deze limiet pauzeert het gebruik meteen.',
  limitHelpValid:
    '{name} heeft deze periode {used} gebruikt. Bij {notice} volgt een melding en bij {limit} wordt het gebruik gepauzeerd.',
  save: 'Opslaan',
  saving: 'Opslaan…',
  removeLimit: 'Limiet verwijderen',
  cancel: 'Annuleren',
  limitSaved: 'De maandlimiet voor {name} is {limit} credits.',
  limitRemoved: '{name} heeft nu geen maandlimiet meer.',
  limitFailed: 'We konden de limiet niet opslaan. Probeer het opnieuw.',
  noteLimits:
    'Een lid dat de limiet bereikt, wordt gepauzeerd tot de volgende verlenging van het plan of tot de eigenaar de limiet verhoogt. Bij 80% krijgt het lid een melding.',
  noteAdmin:
    'Je ziet ieders verbruik omdat je eigenaar of beheerder bent. Leden zien alleen hun eigen verbruik.',
  notePersonal: 'Je ziet je eigen verbruik. Eigenaren en beheerders zien dat van iedereen.',
  summaryCredits: 'Credits',
  summaryShare: 'Aandeel in het bedrijfsverbruik',
  summaryTop: 'Gebruikt het meest',
  summaryTopShare: '{percent} van de eigen credits',
  summaryRoleYou: '{role} · Jij',
  showAllUsers: 'Alle gebruikers tonen',
  planChanged: 'Je plan is bijgewerkt.',
};

const et: Copy = {
  eyebrow: 'Ettevõtte kasutus',
  title: 'Kasutus',
  intro: 'Sinu ettevõtte ühine krediidisaldo ja see, kuidas su meeskond seda kasutab.',
  introPersonal: 'Sinu ettevõtte ühine krediidisaldo ja see, kuidas sina seda kasutad.',
  refresh: 'Värskenda',
  updated: 'Uuendatud kell {time}',
  updatedNow: 'Just uuendatud',
  from: 'Alates',
  to: 'Kuni',
  chooseFirstDay: 'Vali esimene päev',
  chooseLastDay: 'Vali viimane päev',
  previousMonth: 'Eelmine kuu',
  nextMonth: 'Järgmine kuu',
  last7: 'Viimased 7 päeva',
  last30: 'Viimased 30 päeva',
  last90: 'Viimased 90 päeva',
  thisPeriod: 'Praegune periood',
  person: 'Inimene',
  allUsers: 'Kõik kasutajad',
  peopleNoun: 'one:inimene|other:inimest',
  personMenu: 'Näita ühe inimese kulu',
  loading: 'Kasutuse laadimine…',
  unavailable: 'Kasutus pole praegu saadaval.',
  forbidden: 'Sul pole juurdepääsu selle ettevõtte kasutusele.',
  tryAgain: 'Proovi uuesti',
  balanceTitle: 'Krediidisaldo',
  creditsUnit: 'krediiti',
  paused: 'Kasutus peatatud',
  planPerMonth: '{credits} krediiti kuus',
  planRenews: '+{credits} krediiti {date}',
  planSwitches: 'Alates {date}: {credits} krediiti kuus',
  planEnds: 'Pakett lõpeb {date}',
  noPlan: 'Paketti veel pole',
  planPaymentFailed: 'Makse ebaõnnestus',
  planEndedOn: 'Lõppes {date}',
  planEnded: 'Lõppenud',
  openBilling: 'Ava Arveldus',
  summaryTopShareSelf: '{percent} sinu krediitidest',
  discountUntil: '{percent} allahindlust kuni {date}',
  discountOngoing: '{percent} allahindlust',
  neverExpire: 'Krediidid ei aegu kunagi',
  changePlan: 'Muuda paketti',
  upgradeNow: 'Vali suurem pakett',
  choosePlan: 'Vali pakett',
  manageOnWeb: 'Pakette hallatakse Poneva veebiversioonis.',
  noticePlanOwner:
    'Kasutus on peatatud, sest ettevõttel pole aktiivset paketti. Vali pakett, et krediite uuesti kasutada.',
  noticePlanMember:
    'Kasutus on peatatud, sest ettevõttel pole aktiivset paketti. Palu ettevõtte omanikul pakett valida.',
  noticeOutOwner:
    'Oled kõik krediidid ära kasutanud. Vali kohe suurem pakett või oota kuni {date} kell {time}, mil pakett lisab {credits} krediiti.',
  noticeOutMember:
    'Sinu ettevõte on kõik krediidid ära kasutanud. Palu ettevõtte omanikul valida suurem pakett või oota kuni {date} kell {time}, mil pakett lisab {credits} krediiti.',
  noticeOutNoRenewal: 'Oled kõik krediidid ära kasutanud. Vali pakett, et saada juurde.',
  noticeLimit:
    'Oled jõudnud oma kuulimiidini, milleks on {limit} krediiti. Saad krediite uuesti kasutada {date} või varem, kui ettevõtte omanik su limiiti tõstab.',
  noticeLimitSoon:
    'Oled kasutanud {used} oma {limit} kuukrediidist. Kui jõuad {limit} krediidini, kasutus peatatakse.',
  unpriced: '{count} toimingul pole veel hinda ja neid pole nendesse arvudesse arvestatud.',
  balanceAria:
    'Krediidisaldo {from} kuni {to}. Praegu {balance} krediiti. Kasuta nooleklahve, et päevi ükshaaval lugeda.',
  chartAria:
    'Krediidid päevade kaupa {from} kuni {to}. Kasuta nooleklahve, et päevi ükshaaval lugeda.',
  flagRenewed: 'Uuendatud {date}',
  flagNewPlan: 'Uus pakett {date}',
  flagAdded: 'Lisatud {date}',
  nextRenewal: 'uueneb {date}',
  nextNewPlan: 'uus pakett alates {date}',
  today: 'Täna kell {time}',
  soFar: '{day}, seni',
  spent: '{credits} kasutatud',
  spentSoFar: '{credits} seni kasutatud',
  fromNewPlan: '+{credits} uuest paketist',
  atRenewal: '+{credits} uuenemisel',
  added: '+{credits} lisatud',
  balanceValue: '{credits} krediiti',
  creditsInTotal: 'krediiti kokku',
  outOfCredits: 'Krediidid otsas',
  serviceTitle: 'Krediidikulu teenuste kaupa',
  serviceSub: 'Kogu ettevõtte krediidid päevade kaupa',
  serviceSubPersonal: 'Sinu krediidid päevade kaupa',
  chat: 'Vestlus',
  agents: 'Agendid',
  apps: 'Rakendused',
  service: 'Teenus',
  credits: 'Krediidid',
  share: 'Osakaal',
  avgPerDay: 'Keskm. päevas',
  total: 'Kokku',
  empty: 'Sel perioodil krediite ei kasutatud',
  emptyBody: 'Kulu kuvatakse siin, kui su meeskond kasutab Vestlust, Agente ja Rakendusi.',
  userTitle: 'Krediidikulu kasutajate kaupa',
  userSub: 'Enim kasutavate ({count}) ja kõigi teiste krediidid päevade kaupa',
  userSubOne: 'Kasutaja {name} krediidid päevade ja teenuste kaupa',
  everyoneElse: 'Kõik teised ({count})',
  member: 'Liige',
  usedLimit: 'Kasutatud / kuulimiit',
  limitColumn: 'Limiit',
  selectedColumn: 'Valitud',
  you: 'Sina',
  owner: 'Omanik',
  admin: 'Administraator',
  memberRole: 'Liige',
  pausedTag: 'Peatatud',
  usedNoLimit: '{used} kasutatud · Limiiti pole',
  noLimit: 'Limiiti pole',
  ofLimit: '/ {limit}',
  setLimit: 'Määra limiit',
  editLimit: 'Muuda limiiti',
  showSpendFor: 'Näita kasutaja {name} kulu',
  selectedShowAll: '{name} valitud. Näita uuesti kõiki',
  limitFor: 'Kasutaja {name} kuulimiit',
  creditsPerMonth: 'krediiti kuus',
  limitHelp: 'Vali, mitu krediiti saab {name} igas kuus kasutada.',
  limitInvalid: 'Sisesta krediitide arv täisarvuna, vähemalt 1.',
  limitPausesNow:
    '{name} on sel perioodil juba kasutanud {used}, seega peatab see limiit kasutuse kohe.',
  limitHelpValid:
    '{name} on sel perioodil kasutanud {used}. Teade tuleb {notice} juures ja kasutus peatatakse {limit} juures.',
  save: 'Salvesta',
  saving: 'Salvestamine…',
  removeLimit: 'Eemalda limiit',
  cancel: 'Tühista',
  limitSaved: 'Kasutaja {name} kuulimiit on {limit} krediiti.',
  limitRemoved: 'Kasutajal {name} pole enam kuulimiiti.',
  limitFailed: 'Limiiti ei õnnestunud salvestada. Proovi uuesti.',
  noteLimits:
    'Kui liige jõuab oma limiidini, peatatakse tema kasutus kuni paketi järgmise uuenemiseni või kuni omanik limiiti tõstab. Liige saab teate 80% juures.',
  noteAdmin:
    'Näed kõigi kulu, sest oled omanik või administraator. Liikmed näevad ainult enda kulu.',
  notePersonal: 'Näed enda kulu. Omanikud ja administraatorid näevad kõigi kulu.',
  summaryCredits: 'Krediidid',
  summaryShare: 'Osakaal ettevõtte kulust',
  summaryTop: 'Kasutab kõige rohkem',
  summaryTopShare: '{percent} oma krediitidest',
  summaryRoleYou: '{role} · Sina',
  showAllUsers: 'Näita kõiki kasutajaid',
  planChanged: 'Sinu pakett on uuendatud.',
};

const fi: Copy = {
  eyebrow: 'Yrityksen käyttö',
  title: 'Käyttö',
  intro: 'Yrityksesi yhteinen krediittisaldo ja se, miten tiimisi käyttää sitä.',
  introPersonal: 'Yrityksesi yhteinen krediittisaldo ja se, miten käytät sitä.',
  refresh: 'Päivitä',
  updated: 'Päivitetty klo {time}',
  updatedNow: 'Päivitetty juuri',
  from: 'Alku',
  to: 'Loppu',
  chooseFirstDay: 'Valitse ensimmäinen päivä',
  chooseLastDay: 'Valitse viimeinen päivä',
  previousMonth: 'Edellinen kuukausi',
  nextMonth: 'Seuraava kuukausi',
  last7: 'Viimeiset 7 päivää',
  last30: 'Viimeiset 30 päivää',
  last90: 'Viimeiset 90 päivää',
  thisPeriod: 'Nykyinen jakso',
  person: 'Henkilö',
  allUsers: 'Kaikki käyttäjät',
  peopleNoun: 'one:henkilö|other:henkilöä',
  personMenu: 'Näytä yhden henkilön kulutus',
  loading: 'Ladataan käyttöä…',
  unavailable: 'Käyttötiedot eivät ole juuri nyt saatavilla.',
  forbidden: 'Sinulla ei ole pääsyä tämän yrityksen käyttötietoihin.',
  tryAgain: 'Yritä uudelleen',
  balanceTitle: 'Krediittisaldo',
  creditsUnit: 'krediittiä',
  paused: 'Käyttö keskeytetty',
  planPerMonth: '{credits} krediittiä/kk',
  planRenews: '+{credits} krediittiä {date}',
  planSwitches: 'Vaihtuu {date}: {credits} krediittiä/kk',
  planEnds: 'Paketti päättyy {date}',
  noPlan: 'Ei vielä pakettia',
  planPaymentFailed: 'Maksu epäonnistui',
  planEndedOn: 'Päättyi {date}',
  planEnded: 'Päättynyt',
  openBilling: 'Siirry Laskutukseen',
  summaryTopShareSelf: '{percent} krediiteistäsi',
  discountUntil: '{percent}:n alennus {date} asti',
  discountOngoing: '{percent}:n alennus',
  neverExpire: 'Krediitit eivät vanhene koskaan',
  changePlan: 'Vaihda pakettia',
  upgradeNow: 'Vaihda suurempaan',
  choosePlan: 'Valitse paketti',
  manageOnWeb: 'Paketteja hallitaan Ponevan verkkoversiossa.',
  noticePlanOwner:
    'Käyttö on keskeytetty, koska yrityksellä ei ole aktiivista pakettia. Valitse paketti, niin voit taas käyttää krediittejä.',
  noticePlanMember:
    'Käyttö on keskeytetty, koska yrityksellä ei ole aktiivista pakettia. Pyydä yrityksen omistajaa valitsemaan paketti.',
  noticeOutOwner:
    'Olet käyttänyt kaikki krediittisi. Vaihda nyt suurempaan pakettiin tai odota {date} klo {time} asti, jolloin pakettisi lisää {credits} krediittiä.',
  noticeOutMember:
    'Yrityksesi on käyttänyt kaikki krediittinsä. Pyydä yrityksen omistajaa vaihtamaan suurempaan pakettiin tai odota {date} klo {time} asti, jolloin paketti lisää {credits} krediittiä.',
  noticeOutNoRenewal: 'Olet käyttänyt kaikki krediittisi. Valitse paketti saadaksesi lisää.',
  noticeLimit:
    'Olet saavuttanut {limit} krediitin kuukausirajasi. Voit käyttää krediittejä taas {date}, tai aiemmin, jos yrityksen omistaja nostaa rajaasi.',
  noticeLimitSoon:
    'Olet käyttänyt {used} kuukauden {limit} krediitistäsi. Käyttösi keskeytetään, kun raja {limit} täyttyy.',
  unpriced:
    '{count} toimintoa on vielä hinnoittelematta, eikä niitä ole laskettu mukaan näihin lukuihin.',
  balanceAria:
    'Krediittisaldo välillä {from} ja {to}. Saldo nyt {balance} krediittiä. Selaa päiviä nuolinäppäimillä.',
  chartAria: 'Krediitit päivittäin välillä {from} ja {to}. Selaa päiviä nuolinäppäimillä.',
  flagRenewed: 'Uusittu {date}',
  flagNewPlan: 'Uusi paketti {date}',
  flagAdded: 'Lisätty {date}',
  nextRenewal: 'uusiutuu {date}',
  nextNewPlan: 'uusi paketti {date} alkaen',
  today: 'Tänään klo {time}',
  soFar: '{day}, tähän mennessä',
  spent: '{credits} käytetty',
  spentSoFar: '{credits} käytetty tähän mennessä',
  fromNewPlan: '+{credits} uudesta paketista',
  atRenewal: '+{credits} uusiutuessa',
  added: '+{credits} lisätty',
  balanceValue: '{credits} krediittiä',
  creditsInTotal: 'krediittiä yhteensä',
  outOfCredits: 'Krediitit loppuivat',
  serviceTitle: 'Krediittien kulutus palveluittain',
  serviceSub: 'Koko yrityksen krediitit päivittäin',
  serviceSubPersonal: 'Omat krediittisi päivittäin',
  chat: 'Chat',
  agents: 'Agentit',
  apps: 'Sovellukset',
  service: 'Palvelu',
  credits: 'Krediitit',
  share: 'Osuus',
  avgPerDay: 'Keskim. päivässä',
  total: 'Yhteensä',
  empty: 'Krediittejä ei käytetty tällä jaksolla',
  emptyBody: 'Kulutus näkyy täällä, kun tiimisi käyttää Chatia, Agentteja ja Sovelluksia.',
  userTitle: 'Krediittien kulutus käyttäjittäin',
  userSub: 'Eniten käyttävien ({count}) ja kaikkien muiden krediitit päivittäin',
  userSubOne: 'Käyttäjän {name} krediitit päivittäin palveluittain',
  everyoneElse: 'Kaikki muut ({count})',
  member: 'Jäsen',
  usedLimit: 'Käytetty / kuukausiraja',
  limitColumn: 'Raja',
  selectedColumn: 'Valittu',
  you: 'Sinä',
  owner: 'Omistaja',
  admin: 'Ylläpitäjä',
  memberRole: 'Jäsen',
  pausedTag: 'Keskeytetty',
  usedNoLimit: '{used} käytetty · Ei rajaa',
  noLimit: 'Ei rajaa',
  ofLimit: '/ {limit}',
  setLimit: 'Aseta raja',
  editLimit: 'Muokkaa rajaa',
  showSpendFor: 'Näytä käyttäjän {name} kulutus',
  selectedShowAll: '{name} valittu. Näytä taas kaikki',
  limitFor: 'Käyttäjän {name} kuukausiraja',
  creditsPerMonth: 'krediittiä/kk',
  limitHelp: 'Valitse, montako krediittiä {name} voi käyttää kuukaudessa.',
  limitInvalid: 'Anna krediittien määrä kokonaislukuna, vähintään 1.',
  limitPausesNow:
    '{name} on jo käyttänyt {used} tällä jaksolla, joten tämä raja keskeyttää käytön heti.',
  limitHelpValid:
    '{name} on käyttänyt {used} tällä jaksolla. Ilmoitus tulee, kun {notice} täyttyy, ja käyttö keskeytetään, kun {limit} täyttyy.',
  save: 'Tallenna',
  saving: 'Tallennetaan…',
  removeLimit: 'Poista raja',
  cancel: 'Peruuta',
  limitSaved: 'Käyttäjän {name} kuukausiraja on {limit} krediittiä.',
  limitRemoved: 'Käyttäjällä {name} ei ole enää kuukausirajaa.',
  limitFailed: 'Rajan tallennus epäonnistui. Yritä uudelleen.',
  noteLimits:
    'Rajansa saavuttaneen jäsenen käyttö keskeytetään paketin seuraavaan uusiutumiseen asti tai kunnes omistaja nostaa rajaa. Jäsen saa ilmoituksen 80 prosentin kohdalla.',
  noteAdmin:
    'Näet kaikkien kulutuksen, koska olet omistaja tai ylläpitäjä. Jäsenet näkevät vain omansa.',
  notePersonal: 'Näet oman kulutuksesi. Omistajat ja ylläpitäjät näkevät kaikkien kulutuksen.',
  summaryCredits: 'Krediitit',
  summaryShare: 'Osuus yrityksen kulutuksesta',
  summaryTop: 'Käyttää eniten',
  summaryTopShare: '{percent} omista krediiteistä',
  summaryRoleYou: '{role} · Sinä',
  showAllUsers: 'Näytä kaikki käyttäjät',
  planChanged: 'Pakettisi on päivitetty.',
};

const fr: Copy = {
  eyebrow: 'Utilisation de l’entreprise',
  title: 'Utilisation',
  intro:
    'Le solde de crédits partagé de votre entreprise, et la façon dont votre équipe l’utilise.',
  introPersonal:
    'Le solde de crédits partagé de votre entreprise, et la façon dont vous l’utilisez.',
  refresh: 'Actualiser',
  updated: 'Mis à jour à {time}',
  updatedNow: 'Mis à jour à l’instant',
  from: 'Du',
  to: 'Au',
  chooseFirstDay: 'Choisir le premier jour',
  chooseLastDay: 'Choisir le dernier jour',
  previousMonth: 'Mois précédent',
  nextMonth: 'Mois suivant',
  last7: '7 derniers jours',
  last30: '30 derniers jours',
  last90: '90 derniers jours',
  thisPeriod: 'Période en cours',
  person: 'Personne',
  allUsers: 'Tous les utilisateurs',
  peopleNoun: 'one:personne|many:de personnes|other:personnes',
  personMenu: 'Afficher la consommation d’une personne',
  loading: 'Chargement de l’utilisation…',
  unavailable: 'L’utilisation n’est pas disponible pour le moment.',
  forbidden: 'Vous n’avez pas accès à l’utilisation de cette entreprise.',
  tryAgain: 'Réessayer',
  balanceTitle: 'Solde de crédits',
  creditsUnit: 'crédits',
  paused: 'Utilisation suspendue',
  planPerMonth: '{credits} crédits/mois',
  planRenews: '+{credits} crédits le {date}',
  planSwitches: 'Passe à {credits} crédits/mois le {date}',
  planEnds: 'Le forfait prend fin le {date}',
  noPlan: 'Aucun forfait pour l’instant',
  planPaymentFailed: 'Échec du paiement',
  planEndedOn: 'Terminé le {date}',
  planEnded: 'Terminé',
  openBilling: 'Aller à Facturation',
  summaryTopShareSelf: '{percent} de vos crédits',
  discountUntil: '{percent} de réduction jusqu’au {date}',
  discountOngoing: '{percent} de réduction',
  neverExpire: 'Les crédits n’expirent jamais',
  changePlan: 'Changer de forfait',
  upgradeNow: 'Passer au forfait supérieur',
  choosePlan: 'Choisir un forfait',
  manageOnWeb: 'Les forfaits se gèrent dans Poneva sur le web.',
  noticePlanOwner:
    'L’utilisation est suspendue, car l’entreprise n’a aucun forfait actif. Choisissez un forfait pour utiliser à nouveau des crédits.',
  noticePlanMember:
    'L’utilisation est suspendue, car l’entreprise n’a aucun forfait actif. Demandez au propriétaire de l’entreprise d’en choisir un.',
  noticeOutOwner:
    'Vous avez utilisé tous vos crédits. Passez dès maintenant au forfait supérieur, ou attendez le {date} à {time}, quand votre forfait ajoutera {credits} crédits.',
  noticeOutMember:
    'Votre entreprise a utilisé tous ses crédits. Demandez au propriétaire de l’entreprise de passer au forfait supérieur, ou attendez le {date} à {time}, quand le forfait ajoutera {credits} crédits.',
  noticeOutNoRenewal:
    'Vous avez utilisé tous vos crédits. Choisissez un forfait pour en obtenir davantage.',
  noticeLimit:
    'Vous avez atteint votre limite mensuelle de {limit} crédits. Vous pourrez à nouveau utiliser des crédits le {date}, ou plus tôt si le propriétaire de l’entreprise relève votre limite.',
  noticeLimitSoon:
    'Vous avez utilisé {used} de vos {limit} crédits mensuels. Votre utilisation sera suspendue à {limit}.',
  unpriced: '{count} actions n’ont pas encore de prix et ne sont pas comptées dans ces chiffres.',
  balanceAria:
    'Solde de crédits du {from} au {to}. {balance} crédits actuellement. Utilisez les flèches du clavier pour parcourir les jours.',
  chartAria:
    'Crédits par jour du {from} au {to}. Utilisez les flèches du clavier pour parcourir les jours.',
  flagRenewed: 'Renouvelé le {date}',
  flagNewPlan: 'Nouveau forfait le {date}',
  flagAdded: 'Ajoutés le {date}',
  nextRenewal: 'le {date}',
  nextNewPlan: 'nouveau forfait dès le {date}',
  today: 'Aujourd’hui, {time}',
  soFar: '{day}, jusqu’ici',
  spent: '{credits} utilisés',
  spentSoFar: '{credits} utilisés jusqu’ici',
  fromNewPlan: '+{credits} du nouveau forfait',
  atRenewal: '+{credits} au renouvellement',
  added: '+{credits} ajoutés',
  balanceValue: '{credits} crédits',
  creditsInTotal: 'crédits au total',
  outOfCredits: 'Plus de crédits',
  serviceTitle: 'Consommation de crédits par service',
  serviceSub: 'Crédits par jour pour toute l’entreprise',
  serviceSubPersonal: 'Vos crédits par jour',
  chat: 'Chat',
  agents: 'Agents',
  apps: 'Apps',
  service: 'Service',
  credits: 'Crédits',
  share: 'Part',
  avgPerDay: 'Moy. par jour',
  total: 'Total',
  empty: 'Aucun crédit utilisé sur cette période',
  emptyBody: 'La consommation s’affiche ici dès que votre équipe utilise Chat, Agents et Apps.',
  userTitle: 'Consommation de crédits par utilisateur',
  userSub: 'Crédits par jour pour les {count} plus gros utilisateurs et tous les autres',
  userSubOne: 'Crédits par jour et par service pour {name}',
  everyoneElse: 'Tous les autres ({count})',
  member: 'Membre',
  usedLimit: 'Utilisé / limite mensuelle',
  limitColumn: 'Limite',
  selectedColumn: 'Sélection',
  you: 'Vous',
  owner: 'Propriétaire',
  admin: 'Administrateur',
  memberRole: 'Membre',
  pausedTag: 'Suspendu',
  usedNoLimit: '{used} utilisés · Aucune limite',
  noLimit: 'Aucune limite',
  ofLimit: '/ {limit}',
  setLimit: 'Définir une limite',
  editLimit: 'Modifier la limite',
  showSpendFor: 'Afficher la consommation de {name}',
  selectedShowAll: '{name}, sélectionné. Afficher à nouveau tout le monde',
  limitFor: 'Limite mensuelle de {name}',
  creditsPerMonth: 'crédits/mois',
  limitHelp: 'Choisissez combien de crédits {name} peut utiliser chaque mois.',
  limitInvalid: 'Saisissez un nombre entier de crédits, 1 ou plus.',
  limitPausesNow:
    '{name} a déjà utilisé {used} sur cette période, cette limite suspend donc son utilisation immédiatement.',
  limitHelpValid:
    '{name} a utilisé {used} sur cette période. Un avis est envoyé à {notice} et l’utilisation est suspendue à {limit}.',
  save: 'Enregistrer',
  saving: 'Enregistrement…',
  removeLimit: 'Supprimer la limite',
  cancel: 'Annuler',
  limitSaved: 'La limite mensuelle de {name} est de {limit} crédits.',
  limitRemoved: '{name} n’a plus de limite mensuelle.',
  limitFailed: 'Impossible d’enregistrer la limite. Réessayez.',
  noteLimits:
    'Un membre qui atteint sa limite est suspendu jusqu’au prochain renouvellement du forfait ou jusqu’à ce que le propriétaire la relève. Il reçoit un avis à 80 %.',
  noteAdmin:
    'Vous voyez la consommation de tous, car vous êtes propriétaire ou administrateur. Les membres ne voient que la leur.',
  notePersonal:
    'Vous voyez votre propre consommation. Les propriétaires et administrateurs voient celle de tous.',
  summaryCredits: 'Crédits',
  summaryShare: 'Part de la consommation de l’entreprise',
  summaryTop: 'Utilise le plus',
  summaryTopShare: '{percent} de ses crédits',
  summaryRoleYou: '{role} · Vous',
  showAllUsers: 'Afficher tous les utilisateurs',
  planChanged: 'Votre forfait a été mis à jour.',
};

const de: Copy = {
  eyebrow: 'Nutzung im Unternehmen',
  title: 'Nutzung',
  intro: 'Das gemeinsame Credit-Guthaben Ihres Unternehmens und wie Ihr Team es nutzt.',
  introPersonal: 'Das gemeinsame Credit-Guthaben Ihres Unternehmens und wie Sie es nutzen.',
  refresh: 'Aktualisieren',
  updated: 'Aktualisiert um {time}',
  updatedNow: 'Gerade aktualisiert',
  from: 'Von',
  to: 'Bis',
  chooseFirstDay: 'Ersten Tag wählen',
  chooseLastDay: 'Letzten Tag wählen',
  previousMonth: 'Vorheriger Monat',
  nextMonth: 'Nächster Monat',
  last7: 'Letzte 7 Tage',
  last30: 'Letzte 30 Tage',
  last90: 'Letzte 90 Tage',
  thisPeriod: 'Aktueller Zeitraum',
  person: 'Person',
  allUsers: 'Alle Nutzer',
  peopleNoun: 'one:Person|other:Personen',
  personMenu: 'Verbrauch einer Person anzeigen',
  loading: 'Nutzung wird geladen…',
  unavailable: 'Die Nutzung ist gerade nicht verfügbar.',
  forbidden: 'Sie haben keinen Zugriff auf die Nutzung dieses Unternehmens.',
  tryAgain: 'Erneut versuchen',
  balanceTitle: 'Credit-Guthaben',
  creditsUnit: 'Credits',
  paused: 'Nutzung pausiert',
  planPerMonth: '{credits} Credits/Monat',
  planRenews: '+{credits} Credits am {date}',
  planSwitches: 'Wechselt am {date} zu {credits} Credits/Monat',
  planEnds: 'Plan endet am {date}',
  noPlan: 'Noch kein Plan',
  planPaymentFailed: 'Zahlung fehlgeschlagen',
  planEndedOn: 'Endete am {date}',
  planEnded: 'Beendet',
  openBilling: 'Zur Abrechnung',
  summaryTopShareSelf: '{percent} Ihrer Credits',
  discountUntil: '{percent} Rabatt bis {date}',
  discountOngoing: '{percent} Rabatt',
  neverExpire: 'Credits verfallen nie',
  changePlan: 'Plan ändern',
  upgradeNow: 'Jetzt upgraden',
  choosePlan: 'Plan wählen',
  manageOnWeb: 'Pläne werden in Poneva im Web verwaltet.',
  noticePlanOwner:
    'Die Nutzung ist pausiert, weil das Unternehmen keinen aktiven Plan hat. Wählen Sie einen Plan, um wieder Credits zu nutzen.',
  noticePlanMember:
    'Die Nutzung ist pausiert, weil das Unternehmen keinen aktiven Plan hat. Bitten Sie den Inhaber Ihres Unternehmens, einen zu wählen.',
  noticeOutOwner:
    'Sie haben alle Credits aufgebraucht. Upgraden Sie jetzt oder warten Sie bis {date} um {time}, wenn Ihr Plan {credits} Credits hinzufügt.',
  noticeOutMember:
    'Ihr Unternehmen hat alle Credits aufgebraucht. Bitten Sie den Inhaber Ihres Unternehmens um ein Upgrade oder warten Sie bis {date} um {time}, wenn der Plan {credits} Credits hinzufügt.',
  noticeOutNoRenewal:
    'Sie haben alle Credits aufgebraucht. Wählen Sie einen Plan, um weitere zu erhalten.',
  noticeLimit:
    'Sie haben Ihr monatliches Limit von {limit} Credits erreicht. Sie können am {date} wieder Credits nutzen, oder früher, wenn der Inhaber Ihres Unternehmens Ihr Limit erhöht.',
  noticeLimitSoon:
    'Sie haben {used} von {limit} monatlichen Credits genutzt. Bei {limit} wird Ihre Nutzung pausiert.',
  unpriced: '{count} Aktionen haben noch keinen Preis und sind in diesen Zahlen nicht enthalten.',
  balanceAria:
    'Credit-Guthaben von {from} bis {to}. Aktuell {balance} Credits. Mit den Pfeiltasten gehen Sie die Tage einzeln durch.',
  chartAria:
    'Credits pro Tag von {from} bis {to}. Mit den Pfeiltasten gehen Sie die Tage einzeln durch.',
  flagRenewed: 'Verlängert am {date}',
  flagNewPlan: 'Neuer Plan am {date}',
  flagAdded: 'Hinzugefügt am {date}',
  nextRenewal: 'am {date}',
  nextNewPlan: 'neuer Plan ab {date}',
  today: 'Heute, {time}',
  soFar: '{day}, bisher',
  spent: '{credits} verbraucht',
  spentSoFar: '{credits} bisher verbraucht',
  fromNewPlan: '+{credits} aus dem neuen Plan',
  atRenewal: '+{credits} bei der Verlängerung',
  added: '+{credits} hinzugefügt',
  balanceValue: '{credits} Credits',
  creditsInTotal: 'Credits insgesamt',
  outOfCredits: 'Keine Credits mehr',
  serviceTitle: 'Credit-Verbrauch nach Dienst',
  serviceSub: 'Credits pro Tag für das ganze Unternehmen',
  serviceSubPersonal: 'Ihre Credits pro Tag',
  chat: 'Chat',
  agents: 'Agenten',
  apps: 'Apps',
  service: 'Dienst',
  credits: 'Credits',
  share: 'Anteil',
  avgPerDay: 'Ø pro Tag',
  total: 'Gesamt',
  empty: 'In diesem Zeitraum wurden keine Credits genutzt',
  emptyBody: 'Der Verbrauch erscheint hier, sobald Ihr Team Chat, Agenten und Apps nutzt.',
  userTitle: 'Credit-Verbrauch nach Nutzer',
  userSub: 'Credits pro Tag für die Top {count} und alle anderen',
  userSubOne: 'Credits pro Tag und Dienst für {name}',
  everyoneElse: 'Alle anderen ({count})',
  member: 'Mitglied',
  usedLimit: 'Genutzt / Monatslimit',
  limitColumn: 'Limit',
  selectedColumn: 'Ausgewählt',
  you: 'Sie',
  owner: 'Inhaber',
  admin: 'Admin',
  memberRole: 'Mitglied',
  pausedTag: 'Pausiert',
  usedNoLimit: '{used} genutzt · Kein Limit',
  noLimit: 'Kein Limit',
  ofLimit: '/ {limit}',
  setLimit: 'Limit festlegen',
  editLimit: 'Limit bearbeiten',
  showSpendFor: 'Verbrauch von {name} anzeigen',
  selectedShowAll: '{name}, ausgewählt. Wieder alle anzeigen',
  limitFor: 'Monatslimit für {name}',
  creditsPerMonth: 'Credits/Monat',
  limitHelp: 'Legen Sie fest, wie viele Credits {name} pro Monat nutzen kann.',
  limitInvalid: 'Geben Sie eine ganze Zahl an Credits ein, mindestens 1.',
  limitPausesNow:
    '{name} hat in diesem Zeitraum bereits {used} genutzt, daher pausiert dieses Limit die Nutzung sofort.',
  limitHelpValid:
    '{name} hat in diesem Zeitraum {used} genutzt. Bei {notice} folgt ein Hinweis, bei {limit} wird die Nutzung pausiert.',
  save: 'Speichern',
  saving: 'Wird gespeichert…',
  removeLimit: 'Limit entfernen',
  cancel: 'Abbrechen',
  limitSaved: 'Das Monatslimit für {name} beträgt {limit} Credits.',
  limitRemoved: '{name} hat jetzt kein Monatslimit mehr.',
  limitFailed: 'Das Limit konnte nicht gespeichert werden. Versuchen Sie es erneut.',
  noteLimits:
    'Ein Mitglied, das sein Limit erreicht, wird bis zur nächsten Verlängerung des Plans pausiert oder bis der Inhaber das Limit erhöht. Bei 80 % erhält es einen Hinweis.',
  noteAdmin:
    'Sie sehen den Verbrauch aller, weil Sie Inhaber oder Admin sind. Mitglieder sehen nur ihren eigenen.',
  notePersonal: 'Sie sehen Ihren eigenen Verbrauch. Inhaber und Admins sehen den Verbrauch aller.',
  summaryCredits: 'Credits',
  summaryShare: 'Anteil am Verbrauch des Unternehmens',
  summaryTop: 'Nutzt am meisten',
  summaryTopShare: '{percent} der eigenen Credits',
  summaryRoleYou: '{role} · Sie',
  showAllUsers: 'Alle Nutzer anzeigen',
  planChanged: 'Ihr Plan wurde aktualisiert.',
};

const el: Copy = {
  eyebrow: 'Χρήση εταιρείας',
  title: 'Χρήση',
  intro: 'Το κοινό υπόλοιπο πιστώσεων της εταιρείας σας και πώς το χρησιμοποιεί η ομάδα σας.',
  introPersonal: 'Το κοινό υπόλοιπο πιστώσεων της εταιρείας σας και πώς το χρησιμοποιείτε εσείς.',
  refresh: 'Ανανέωση',
  updated: 'Ενημερώθηκε στις {time}',
  updatedNow: 'Ενημερώθηκε μόλις τώρα',
  from: 'Από',
  to: 'Έως',
  chooseFirstDay: 'Επιλέξτε την πρώτη ημέρα',
  chooseLastDay: 'Επιλέξτε την τελευταία ημέρα',
  previousMonth: 'Προηγούμενος μήνας',
  nextMonth: 'Επόμενος μήνας',
  last7: 'Τελευταίες 7 ημέρες',
  last30: 'Τελευταίες 30 ημέρες',
  last90: 'Τελευταίες 90 ημέρες',
  thisPeriod: 'Τρέχουσα περίοδος',
  person: 'Άτομο',
  allUsers: 'Όλοι οι χρήστες',
  peopleNoun: 'one:άτομο|other:άτομα',
  personMenu: 'Εμφάνιση κατανάλωσης για ένα άτομο',
  loading: 'Φόρτωση χρήσης…',
  unavailable: 'Η χρήση δεν είναι διαθέσιμη αυτή τη στιγμή.',
  forbidden: 'Δεν έχετε πρόσβαση στη χρήση αυτής της εταιρείας.',
  tryAgain: 'Δοκιμάστε ξανά',
  balanceTitle: 'Υπόλοιπο πιστώσεων',
  creditsUnit: 'πιστώσεις',
  paused: 'Χρήση σε παύση',
  planPerMonth: '{credits} πιστώσεις/μήνα',
  planRenews: '+{credits} πιστώσεις στις {date}',
  planSwitches: 'Αλλάζει σε {credits} πιστώσεις/μήνα στις {date}',
  planEnds: 'Το πρόγραμμα λήγει στις {date}',
  noPlan: 'Δεν υπάρχει ακόμη πρόγραμμα',
  planPaymentFailed: 'Η πληρωμή απέτυχε',
  planEndedOn: 'Έληξε στις {date}',
  planEnded: 'Έληξε',
  openBilling: 'Μετάβαση στη Χρέωση',
  summaryTopShareSelf: '{percent} των πιστώσεών σας',
  discountUntil: 'Έκπτωση {percent} έως {date}',
  discountOngoing: 'Έκπτωση {percent}',
  neverExpire: 'Οι πιστώσεις δεν λήγουν ποτέ',
  changePlan: 'Αλλαγή προγράμματος',
  upgradeNow: 'Αναβάθμιση τώρα',
  choosePlan: 'Επιλογή προγράμματος',
  manageOnWeb: 'Η διαχείριση των προγραμμάτων γίνεται στην έκδοση web του Poneva.',
  noticePlanOwner:
    'Η χρήση είναι σε παύση επειδή η εταιρεία δεν έχει ενεργό πρόγραμμα. Επιλέξτε πρόγραμμα για να χρησιμοποιήσετε ξανά πιστώσεις.',
  noticePlanMember:
    'Η χρήση είναι σε παύση επειδή η εταιρεία δεν έχει ενεργό πρόγραμμα. Ζητήστε από τον κάτοχο της εταιρείας να επιλέξει ένα.',
  noticeOutOwner:
    'Χρησιμοποιήσατε όλες τις πιστώσεις σας. Κάντε αναβάθμιση τώρα ή περιμένετε έως τις {date} στις {time}, όταν το πρόγραμμά σας θα προσθέσει {credits} πιστώσεις.',
  noticeOutMember:
    'Η εταιρεία σας χρησιμοποίησε όλες τις πιστώσεις της. Ζητήστε από τον κάτοχο της εταιρείας να κάνει αναβάθμιση ή περιμένετε έως τις {date} στις {time}, όταν το πρόγραμμα θα προσθέσει {credits} πιστώσεις.',
  noticeOutNoRenewal:
    'Χρησιμοποιήσατε όλες τις πιστώσεις σας. Επιλέξτε πρόγραμμα για να αποκτήσετε περισσότερες.',
  noticeLimit:
    'Φτάσατε το μηνιαίο όριό σας των {limit} πιστώσεων. Μπορείτε να χρησιμοποιήσετε ξανά πιστώσεις στις {date} ή νωρίτερα, αν ο κάτοχος της εταιρείας αυξήσει το όριό σας.',
  noticeLimitSoon:
    'Χρησιμοποιήσατε {used} από τις {limit} μηνιαίες πιστώσεις σας. Η χρήση σας θα τεθεί σε παύση όταν φτάσετε τις {limit}.',
  unpriced:
    '{count} ενέργειες δεν έχουν ακόμη τιμή και δεν περιλαμβάνονται σε αυτούς τους αριθμούς.',
  balanceAria:
    'Υπόλοιπο πιστώσεων από {from} έως {to}. Τώρα {balance} πιστώσεις. Χρησιμοποιήστε τα πλήκτρα βέλους για να διαβάσετε κάθε ημέρα.',
  chartAria:
    'Ημερήσιες πιστώσεις από {from} έως {to}. Χρησιμοποιήστε τα πλήκτρα βέλους για να διαβάσετε κάθε ημέρα.',
  flagRenewed: 'Ανανέωση {date}',
  flagNewPlan: 'Νέο πρόγραμμα {date}',
  flagAdded: 'Προσθήκη {date}',
  nextRenewal: 'στις {date}',
  nextNewPlan: 'νέο πρόγραμμα από {date}',
  today: 'Σήμερα, {time}',
  soFar: '{day}, μέχρι στιγμής',
  spent: '{credits} καταναλώθηκαν',
  spentSoFar: '{credits} καταναλώθηκαν μέχρι στιγμής',
  fromNewPlan: '+{credits} από το νέο πρόγραμμα',
  atRenewal: '+{credits} κατά την ανανέωση',
  added: '+{credits} προστέθηκαν',
  balanceValue: '{credits} πιστώσεις',
  creditsInTotal: 'πιστώσεις συνολικά',
  outOfCredits: 'Οι πιστώσεις εξαντλήθηκαν',
  serviceTitle: 'Κατανάλωση πιστώσεων ανά υπηρεσία',
  serviceSub: 'Ημερήσιες πιστώσεις για όλη την εταιρεία',
  serviceSubPersonal: 'Οι ημερήσιες πιστώσεις σας',
  chat: 'Συνομιλία',
  agents: 'Πράκτορες',
  apps: 'Εφαρμογές',
  service: 'Υπηρεσία',
  credits: 'Πιστώσεις',
  share: 'Ποσοστό',
  avgPerDay: 'Μ.Ο. ανά ημέρα',
  total: 'Σύνολο',
  empty: 'Δεν χρησιμοποιήθηκαν πιστώσεις σε αυτή την περίοδο',
  emptyBody:
    'Η κατανάλωση εμφανίζεται εδώ όταν η ομάδα σας χρησιμοποιεί Συνομιλία, Πράκτορες και Εφαρμογές.',
  userTitle: 'Κατανάλωση πιστώσεων ανά χρήστη',
  userSub: 'Ημερήσιες πιστώσεις για τους {count} πιο ενεργούς και όλους τους άλλους',
  userSubOne: 'Ημερήσιες πιστώσεις ανά υπηρεσία για {name}',
  everyoneElse: 'Όλοι οι άλλοι ({count})',
  member: 'Μέλος',
  usedLimit: 'Χρήση / μηνιαίο όριο',
  limitColumn: 'Όριο',
  selectedColumn: 'Επιλεγμένο',
  you: 'Εσείς',
  owner: 'Κάτοχος',
  admin: 'Διαχειριστής',
  memberRole: 'Μέλος',
  pausedTag: 'Σε παύση',
  usedNoLimit: '{used} χρησιμοποιήθηκαν · Χωρίς όριο',
  noLimit: 'Χωρίς όριο',
  ofLimit: '/ {limit}',
  setLimit: 'Ορισμός ορίου',
  editLimit: 'Επεξεργασία ορίου',
  showSpendFor: 'Εμφάνιση κατανάλωσης για {name}',
  selectedShowAll: 'Επιλέχθηκε: {name}. Εμφάνιση όλων ξανά',
  limitFor: 'Μηνιαίο όριο για {name}',
  creditsPerMonth: 'πιστώσεις/μήνα',
  limitHelp: 'Επιλέξτε πόσες πιστώσεις μπορεί να χρησιμοποιεί κάθε μήνα ο χρήστης {name}.',
  limitInvalid: 'Εισαγάγετε ακέραιο αριθμό πιστώσεων, 1 ή περισσότερες.',
  limitPausesNow:
    'Ο χρήστης {name} έχει ήδη χρησιμοποιήσει {used} σε αυτή την περίοδο, οπότε αυτό το όριο θέτει αμέσως τη χρήση του σε παύση.',
  limitHelpValid:
    'Ο χρήστης {name} έχει χρησιμοποιήσει {used} σε αυτή την περίοδο. Θα λάβει ειδοποίηση στις {notice} και η χρήση του θα τεθεί σε παύση στις {limit}.',
  save: 'Αποθήκευση',
  saving: 'Αποθήκευση…',
  removeLimit: 'Κατάργηση ορίου',
  cancel: 'Ακύρωση',
  limitSaved: 'Το μηνιαίο όριο για {name} είναι {limit} πιστώσεις.',
  limitRemoved: 'Ο χρήστης {name} δεν έχει πλέον μηνιαίο όριο.',
  limitFailed: 'Δεν ήταν δυνατή η αποθήκευση του ορίου. Δοκιμάστε ξανά.',
  noteLimits:
    'Ένα μέλος που φτάνει το όριό του τίθεται σε παύση έως την επόμενη ανανέωση του προγράμματος ή μέχρι ο κάτοχος να αυξήσει το όριο. Λαμβάνει ειδοποίηση στο 80%.',
  noteAdmin:
    'Βλέπετε την κατανάλωση όλων επειδή είστε κάτοχος ή διαχειριστής. Τα μέλη βλέπουν μόνο τη δική τους.',
  notePersonal:
    'Βλέπετε τη δική σας κατανάλωση. Οι κάτοχοι και οι διαχειριστές βλέπουν την κατανάλωση όλων.',
  summaryCredits: 'Πιστώσεις',
  summaryShare: 'Μερίδιο στην κατανάλωση της εταιρείας',
  summaryTop: 'Χρησιμοποιεί περισσότερο',
  summaryTopShare: '{percent} των δικών του πιστώσεων',
  summaryRoleYou: '{role} · Εσείς',
  showAllUsers: 'Εμφάνιση όλων των χρηστών',
  planChanged: 'Το πρόγραμμά σας ενημερώθηκε.',
};

const hu: Copy = {
  eyebrow: 'Vállalati használat',
  title: 'Használat',
  intro: 'Vállalata közös kreditegyenlege, és hogy csapata hogyan használja.',
  introPersonal: 'Vállalata közös kreditegyenlege, és hogy Ön hogyan használja.',
  refresh: 'Frissítés',
  updated: 'Frissítve: {time}',
  updatedNow: 'Épp most frissítve',
  from: 'Kezdete',
  to: 'Vége',
  chooseFirstDay: 'Válassza ki az első napot',
  chooseLastDay: 'Válassza ki az utolsó napot',
  previousMonth: 'Előző hónap',
  nextMonth: 'Következő hónap',
  last7: 'Elmúlt 7 nap',
  last30: 'Elmúlt 30 nap',
  last90: 'Elmúlt 90 nap',
  thisPeriod: 'Aktuális időszak',
  person: 'Személy',
  allUsers: 'Minden felhasználó',
  peopleNoun: 'one:személy|other:személy',
  personMenu: 'Egy személy felhasználásának megjelenítése',
  loading: 'Használat betöltése…',
  unavailable: 'A használati adatok jelenleg nem érhetők el.',
  forbidden: 'Nincs hozzáférése a vállalat használati adataihoz.',
  tryAgain: 'Újrapróbálkozás',
  balanceTitle: 'Kreditegyenleg',
  creditsUnit: 'kredit',
  paused: 'Használat szüneteltetve',
  planPerMonth: '{credits} kredit/hó',
  planRenews: '+{credits} kredit: {date}',
  planSwitches: 'Váltás {credits} kredit/hó csomagra: {date}',
  planEnds: 'A csomag lejár: {date}',
  noPlan: 'Még nincs csomag',
  planPaymentFailed: 'Sikertelen fizetés',
  planEndedOn: 'Véget ért: {date}',
  planEnded: 'Véget ért',
  openBilling: 'Ugrás a Számlázáshoz',
  summaryTopShareSelf: 'az Ön kreditjeinek {percent}-a',
  discountUntil: '{percent} kedvezmény eddig: {date}',
  discountOngoing: '{percent} kedvezmény',
  neverExpire: 'A kreditek soha nem járnak le',
  changePlan: 'Csomag módosítása',
  upgradeNow: 'Váltás nagyobb csomagra',
  choosePlan: 'Csomag kiválasztása',
  manageOnWeb: 'A csomagokat a Poneva webes felületén kezelheti.',
  noticePlanOwner:
    'A használat szünetel, mert a vállalatnak nincs aktív csomagja. Válasszon csomagot, hogy újra használhassa a krediteket.',
  noticePlanMember:
    'A használat szünetel, mert a vállalatnak nincs aktív csomagja. Kérje meg a vállalat tulajdonosát, hogy válasszon egyet.',
  noticeOutOwner:
    'Az összes kreditjét felhasználta. Váltson most nagyobb csomagra, vagy várjon eddig: {date} {time}. Ekkor a csomag {credits} kreditet ad hozzá.',
  noticeOutMember:
    'A vállalat az összes kreditjét felhasználta. Kérje meg a vállalat tulajdonosát, hogy váltson nagyobb csomagra, vagy várjon eddig: {date} {time}. Ekkor a csomag {credits} kreditet ad hozzá.',
  noticeOutNoRenewal: 'Az összes kreditjét felhasználta. Válasszon csomagot, hogy többet kapjon.',
  noticeLimit:
    'Elérte a havi {limit} kredites keretét. Újra használhat krediteket ekkor: {date}, vagy korábban, ha a vállalat tulajdonosa megemeli a keretét.',
  noticeLimitSoon:
    'Felhasznált {used} kreditet a havi {limit} kreditből. {limit} kreditnél a használat szünetel.',
  unpriced: '{count} műveletnek még nincs ára, ezért nem szerepel ezekben a számokban.',
  balanceAria:
    'Kreditegyenleg {from} és {to} között. Jelenleg {balance} kredit. A nyílbillentyűkkel napról napra haladhat.',
  chartAria: 'Napi kreditek {from} és {to} között. A nyílbillentyűkkel napról napra haladhat.',
  flagRenewed: 'Megújítva: {date}',
  flagNewPlan: 'Új csomag: {date}',
  flagAdded: 'Hozzáadva: {date}',
  nextRenewal: 'megújul: {date}',
  nextNewPlan: 'új csomag: {date}',
  today: 'Ma, {time}',
  soFar: '{day}, eddig',
  spent: '{credits} elhasználva',
  spentSoFar: '{credits} elhasználva eddig',
  fromNewPlan: '+{credits} az új csomagból',
  atRenewal: '+{credits} megújításkor',
  added: '+{credits} hozzáadva',
  balanceValue: '{credits} kredit',
  creditsInTotal: 'kredit összesen',
  outOfCredits: 'Elfogytak a kreditek',
  serviceTitle: 'Kreditfelhasználás szolgáltatásonként',
  serviceSub: 'Napi kreditek a teljes vállalatra',
  serviceSubPersonal: 'Az Ön napi kreditjei',
  chat: 'Csevegés',
  agents: 'Ügynökök',
  apps: 'Alkalmazások',
  service: 'Szolgáltatás',
  credits: 'Kreditek',
  share: 'Arány',
  avgPerDay: 'Napi átlag',
  total: 'Összesen',
  empty: 'Ebben az időszakban nem használtak fel kreditet',
  emptyBody:
    'A felhasználás itt jelenik meg, amikor csapata a Csevegést, az Ügynököket és az Alkalmazásokat használja.',
  userTitle: 'Kreditfelhasználás felhasználónként',
  userSub: 'Napi kreditek a {count} legaktívabb felhasználóra és mindenki másra',
  userSubOne: 'Napi kreditek szolgáltatásonként: {name}',
  everyoneElse: 'Mindenki más ({count})',
  member: 'Tag',
  usedLimit: 'Felhasznált / havi keret',
  limitColumn: 'Keret',
  selectedColumn: 'Kijelölve',
  you: 'Ön',
  owner: 'Tulajdonos',
  admin: 'Adminisztrátor',
  memberRole: 'Tag',
  pausedTag: 'Szüneteltetve',
  usedNoLimit: '{used} felhasználva · Nincs keret',
  noLimit: 'Nincs keret',
  ofLimit: '/ {limit}',
  setLimit: 'Keret beállítása',
  editLimit: 'Keret módosítása',
  showSpendFor: 'Felhasználás megjelenítése: {name}',
  selectedShowAll: '{name} kijelölve. Mindenki megjelenítése újra',
  limitFor: 'Havi keret: {name}',
  creditsPerMonth: 'kredit/hó',
  limitHelp: 'Adja meg, hány kreditet használhat fel {name} havonta.',
  limitInvalid: 'Adjon meg egész számot, legalább 1-et.',
  limitPausesNow:
    '{name} ebben az időszakban már {used} kreditet felhasznált, így ez a keret azonnal szünetelteti a használatát.',
  limitHelpValid:
    '{name} ebben az időszakban {used} kreditet használt fel. {notice} kreditnél értesítést kap, {limit} kreditnél pedig a használata szünetel.',
  save: 'Mentés',
  saving: 'Mentés…',
  removeLimit: 'Keret eltávolítása',
  cancel: 'Mégse',
  limitSaved: '{name} havi kerete: {limit} kredit.',
  limitRemoved: '{name} számára már nincs havi keret.',
  limitFailed: 'Nem sikerült menteni a keretet. Próbálja újra.',
  noteLimits:
    'Ha egy tag eléri a keretét, a használata a csomag következő megújításáig szünetel, vagy amíg a tulajdonos meg nem emeli a keretet. 80%-nál értesítést kap.',
  noteAdmin:
    'Mindenki felhasználását látja, mert Ön tulajdonos vagy adminisztrátor. A tagok csak a sajátjukat látják.',
  notePersonal:
    'A saját felhasználását látja. A tulajdonosok és adminisztrátorok mindenkiét látják.',
  summaryCredits: 'Kreditek',
  summaryShare: 'Részesedés a vállalati felhasználásból',
  summaryTop: 'Leginkább használt',
  summaryTopShare: 'saját kreditjeinek {percent}-a',
  summaryRoleYou: '{role} · Ön',
  showAllUsers: 'Minden felhasználó megjelenítése',
  planChanged: 'A csomag frissült.',
};

const ga: Copy = {
  eyebrow: 'Úsáid na cuideachta',
  title: 'Úsáid',
  intro:
    'Iarmhéid creidmheasanna comhroinnte do chuideachta, agus an chaoi a n-úsáideann d’fhoireann é.',
  introPersonal:
    'Iarmhéid creidmheasanna comhroinnte do chuideachta, agus an chaoi a n-úsáideann tú é.',
  refresh: 'Athnuaigh',
  updated: 'Nuashonraithe ag {time}',
  updatedNow: 'Nuashonraithe anois díreach',
  from: 'Ó',
  to: 'Go',
  chooseFirstDay: 'Roghnaigh an chéad lá',
  chooseLastDay: 'Roghnaigh an lá deireanach',
  previousMonth: 'An mhí roimhe',
  nextMonth: 'An chéad mhí eile',
  last7: 'Na 7 lá deireanacha',
  last30: 'Na 30 lá deireanacha',
  last90: 'Na 90 lá deireanacha',
  thisPeriod: 'An tréimhse reatha',
  person: 'Duine',
  allUsers: 'Gach úsáideoir',
  peopleNoun: 'one:duine|two:dhuine|few:dhuine|many:nduine|other:duine',
  personMenu: 'Taispeáin caiteachas duine amháin',
  loading: 'An úsáid á lódáil…',
  unavailable: 'Níl an úsáid ar fáil faoi láthair.',
  forbidden: 'Níl rochtain agat ar úsáid na cuideachta seo.',
  tryAgain: 'Bain triail eile as',
  balanceTitle: 'Iarmhéid creidmheasanna',
  creditsUnit: 'creidmheas',
  paused: 'Úsáid ar sos',
  planPerMonth: '{credits} creidmheas sa mhí',
  planRenews: '+{credits} creidmheas ar {date}',
  planSwitches: 'Athrú go {credits} creidmheas sa mhí ar {date}',
  planEnds: 'Críochnaíonn an plean ar {date}',
  noPlan: 'Níl plean ann fós',
  planPaymentFailed: 'Theip ar an íocaíocht',
  planEndedOn: 'Críochnaithe ar {date}',
  planEnded: 'Críochnaithe',
  openBilling: 'Téigh go Billeáil',
  summaryTopShareSelf: '{percent} de do chreidmheasanna',
  discountUntil: '{percent} lascaine go dtí {date}',
  discountOngoing: '{percent} lascaine',
  neverExpire: 'Ní théann creidmheasanna in éag riamh',
  changePlan: 'Athraigh an plean',
  upgradeNow: 'Uasghrádaigh anois',
  choosePlan: 'Roghnaigh plean',
  manageOnWeb: 'Bainistítear pleananna in Poneva ar an ngréasán.',
  noticePlanOwner:
    'Tá an úsáid ar sos mar níl plean gníomhach ag an gcuideachta. Roghnaigh plean chun creidmheasanna a úsáid arís.',
  noticePlanMember:
    'Tá an úsáid ar sos mar níl plean gníomhach ag an gcuideachta. Iarr ar úinéir na cuideachta ceann a roghnú.',
  noticeOutOwner:
    'D’úsáid tú do chreidmheasanna go léir. Uasghrádaigh anois, nó fan go dtí {date} ag {time} nuair a chuirfidh do phlean {credits} creidmheas leis.',
  noticeOutMember:
    'D’úsáid do chuideachta a creidmheasanna go léir. Iarr ar úinéir na cuideachta uasghrádú, nó fan go dtí {date} ag {time} nuair a chuirfidh an plean {credits} creidmheas leis.',
  noticeOutNoRenewal:
    'D’úsáid tú do chreidmheasanna go léir. Roghnaigh plean chun tuilleadh a fháil.',
  noticeLimit:
    'Shroich tú do theorainn mhíosúil de {limit} creidmheas. Is féidir leat creidmheasanna a úsáid arís ar {date}, nó níos luaithe má ardaíonn úinéir na cuideachta do theorainn.',
  noticeLimitSoon:
    'D’úsáid tú {used} de do {limit} creidmheas míosúil. Cuirfear ar sos thú nuair a shroicheann tú {limit}.',
  unpriced: 'Gníomhartha nach bhfuil praghas orthu fós agus nach bhfuil sna figiúirí seo: {count}.',
  balanceAria:
    'Iarmhéid creidmheasanna ó {from} go {to}. {balance} creidmheas anois. Úsáid na saigheadeochracha chun gach lá a léamh.',
  chartAria:
    'Creidmheasanna laethúla ó {from} go {to}. Úsáid na saigheadeochracha chun gach lá a léamh.',
  flagRenewed: 'Athnuaite {date}',
  flagNewPlan: 'Plean nua {date}',
  flagAdded: 'Curtha leis {date}',
  nextRenewal: 'ar {date}',
  nextNewPlan: 'plean nua ó {date}',
  today: 'Inniu, {time}',
  soFar: '{day}, go dtí seo',
  spent: '{credits} caite',
  spentSoFar: '{credits} caite go dtí seo',
  fromNewPlan: '+{credits} ón bplean nua',
  atRenewal: '+{credits} ag an athnuachan',
  added: '+{credits} curtha leis',
  balanceValue: '{credits} creidmheas',
  creditsInTotal: 'creidmheas san iomlán',
  outOfCredits: 'Gan chreidmheasanna',
  serviceTitle: 'Caiteachas creidmheasanna de réir seirbhíse',
  serviceSub: 'Creidmheasanna laethúla don chuideachta ar fad',
  serviceSubPersonal: 'Do chreidmheasanna laethúla',
  chat: 'Comhrá',
  agents: 'Gníomhairí',
  apps: 'Aipeanna',
  service: 'Seirbhís',
  credits: 'Creidmheasanna',
  share: 'Sciar',
  avgPerDay: 'Meán in aghaidh an lae',
  total: 'Iomlán',
  empty: 'Níor úsáideadh creidmheasanna sa tréimhse seo',
  emptyBody:
    'Taispeántar caiteachas anseo nuair a úsáideann d’fhoireann Comhrá, Gníomhairí agus Aipeanna.',
  userTitle: 'Caiteachas creidmheasanna de réir úsáideora',
  userSub: 'Creidmheasanna laethúla do na húsáideoirí is gníomhaí ({count}) agus do chách eile',
  userSubOne: 'Creidmheasanna laethúla de réir seirbhíse do {name}',
  everyoneElse: 'Cách eile ({count})',
  member: 'Ball',
  usedLimit: 'Úsáidte / teorainn mhíosúil',
  limitColumn: 'Teorainn',
  selectedColumn: 'Roghnaithe',
  you: 'Tusa',
  owner: 'Úinéir',
  admin: 'Riarthóir',
  memberRole: 'Ball',
  pausedTag: 'Ar sos',
  usedNoLimit: '{used} úsáidte · Gan teorainn',
  noLimit: 'Gan teorainn',
  ofLimit: '/ {limit}',
  setLimit: 'Socraigh teorainn',
  editLimit: 'Cuir an teorainn in eagar',
  showSpendFor: 'Taispeáin caiteachas {name}',
  selectedShowAll: '{name}, roghnaithe. Taispeáin gach duine arís',
  limitFor: 'Teorainn mhíosúil do {name}',
  creditsPerMonth: 'creidmheas sa mhí',
  limitHelp: 'Roghnaigh cé mhéad creidmheas is féidir le {name} a úsáid gach mí.',
  limitInvalid: 'Cuir isteach slánuimhir creidmheasanna, 1 nó níos mó.',
  limitPausesNow:
    'D’úsáid {name} {used} cheana féin sa tréimhse seo, mar sin cuirfidh an teorainn seo an úsáid ar sos láithreach.',
  limitHelpValid:
    'D’úsáid {name} {used} sa tréimhse seo. Seoltar fógra ag {notice} agus cuirtear an úsáid ar sos ag {limit}.',
  save: 'Sábháil',
  saving: 'Á shábháil…',
  removeLimit: 'Bain an teorainn',
  cancel: 'Cealaigh',
  limitSaved: 'Is é {limit} creidmheas an teorainn mhíosúil do {name}.',
  limitRemoved: 'Níl teorainn mhíosúil ag {name} anois.',
  limitFailed: 'Níorbh fhéidir an teorainn a shábháil. Bain triail eile as.',
  noteLimits:
    'Nuair a shroicheann ball a theorainn, cuirtear ar sos é go dtí an chéad athnuachan eile ar an bplean nó go dtí go n-ardaíonn an t-úinéir í. Faigheann sé fógra ag 80%.',
  noteAdmin:
    'Feiceann tú caiteachas gach duine mar is úinéir nó riarthóir thú. Ní fheiceann baill ach a gcaiteachas féin.',
  notePersonal:
    'Feiceann tú do chaiteachas féin. Feiceann úinéirí agus riarthóirí caiteachas gach duine.',
  summaryCredits: 'Creidmheasanna',
  summaryShare: 'Sciar de chaiteachas na cuideachta',
  summaryTop: 'Is mó a úsáidtear',
  summaryTopShare: '{percent} dá gcreidmheasanna féin',
  summaryRoleYou: '{role} · Tusa',
  showAllUsers: 'Taispeáin gach úsáideoir',
  planChanged: 'Tá do phlean nuashonraithe.',
};

const it: Copy = {
  eyebrow: 'Utilizzo dell’azienda',
  title: 'Utilizzo',
  intro: 'Il saldo crediti condiviso della tua azienda e come lo usa il tuo team.',
  introPersonal: 'Il saldo crediti condiviso della tua azienda e come lo usi tu.',
  refresh: 'Aggiorna',
  updated: 'Aggiornato alle {time}',
  updatedNow: 'Aggiornato ora',
  from: 'Dal',
  to: 'Al',
  chooseFirstDay: 'Scegli il primo giorno',
  chooseLastDay: 'Scegli l’ultimo giorno',
  previousMonth: 'Mese precedente',
  nextMonth: 'Mese successivo',
  last7: 'Ultimi 7 giorni',
  last30: 'Ultimi 30 giorni',
  last90: 'Ultimi 90 giorni',
  thisPeriod: 'Periodo attuale',
  person: 'Persona',
  allUsers: 'Tutti gli utenti',
  peopleNoun: 'one:persona|many:di persone|other:persone',
  personMenu: 'Mostra il consumo di una persona',
  loading: 'Caricamento dell’utilizzo…',
  unavailable: 'L’utilizzo non è disponibile al momento.',
  forbidden: 'Non hai accesso all’utilizzo di questa azienda.',
  tryAgain: 'Riprova',
  balanceTitle: 'Saldo crediti',
  creditsUnit: 'crediti',
  paused: 'Utilizzo in pausa',
  planPerMonth: '{credits} crediti/mese',
  planRenews: '+{credits} crediti il {date}',
  planSwitches: 'Passa a {credits} crediti/mese il {date}',
  planEnds: 'Il piano termina il {date}',
  noPlan: 'Ancora nessun piano',
  planPaymentFailed: 'Pagamento non riuscito',
  planEndedOn: 'Terminato il {date}',
  planEnded: 'Terminato',
  openBilling: 'Vai a Fatturazione',
  summaryTopShareSelf: '{percent} dei tuoi crediti',
  discountUntil: '{percent} di sconto fino al {date}',
  discountOngoing: '{percent} di sconto',
  neverExpire: 'I crediti non scadono mai',
  changePlan: 'Cambia piano',
  upgradeNow: 'Passa a un piano superiore',
  choosePlan: 'Scegli un piano',
  manageOnWeb: 'I piani si gestiscono in Poneva sul web.',
  noticePlanOwner:
    'L’utilizzo è in pausa perché l’azienda non ha un piano attivo. Scegli un piano per tornare a usare i crediti.',
  noticePlanMember:
    'L’utilizzo è in pausa perché l’azienda non ha un piano attivo. Chiedi al proprietario dell’azienda di sceglierne uno.',
  noticeOutOwner:
    'Hai usato tutti i tuoi crediti. Passa ora a un piano superiore, oppure attendi il {date} alle {time}, quando il tuo piano aggiungerà {credits} crediti.',
  noticeOutMember:
    'La tua azienda ha usato tutti i suoi crediti. Chiedi al proprietario dell’azienda di passare a un piano superiore, oppure attendi il {date} alle {time}, quando il piano aggiungerà {credits} crediti.',
  noticeOutNoRenewal: 'Hai usato tutti i tuoi crediti. Scegli un piano per averne altri.',
  noticeLimit:
    'Hai raggiunto il tuo limite mensile di {limit} crediti. Potrai usare di nuovo i crediti il {date}, o prima se il proprietario dell’azienda aumenta il tuo limite.',
  noticeLimitSoon:
    'Hai usato {used} dei tuoi {limit} crediti mensili. Al raggiungimento di {limit} il tuo utilizzo verrà messo in pausa.',
  unpriced: '{count} azioni non hanno ancora un prezzo e sono escluse da questi numeri.',
  balanceAria:
    'Saldo crediti dal {from} al {to}. Ora {balance} crediti. Usa i tasti freccia per scorrere i giorni.',
  chartAria: 'Crediti giornalieri dal {from} al {to}. Usa i tasti freccia per scorrere i giorni.',
  flagRenewed: 'Rinnovato il {date}',
  flagNewPlan: 'Nuovo piano il {date}',
  flagAdded: 'Aggiunti il {date}',
  nextRenewal: 'il {date}',
  nextNewPlan: 'nuovo piano dal {date}',
  today: 'Oggi, {time}',
  soFar: '{day}, finora',
  spent: '{credits} usati',
  spentSoFar: '{credits} usati finora',
  fromNewPlan: '+{credits} dal nuovo piano',
  atRenewal: '+{credits} al rinnovo',
  added: '+{credits} aggiunti',
  balanceValue: '{credits} crediti',
  creditsInTotal: 'crediti in totale',
  outOfCredits: 'Crediti esauriti',
  serviceTitle: 'Consumo di crediti per servizio',
  serviceSub: 'Crediti giornalieri di tutta l’azienda',
  serviceSubPersonal: 'I tuoi crediti giornalieri',
  chat: 'Chat',
  agents: 'Agenti',
  apps: 'App',
  service: 'Servizio',
  credits: 'Crediti',
  share: 'Quota',
  avgPerDay: 'Media giornaliera',
  total: 'Totale',
  empty: 'Nessun credito usato in questo periodo',
  emptyBody: 'Il consumo compare qui quando il tuo team usa Chat, Agenti e App.',
  userTitle: 'Consumo di crediti per utente',
  userSub: 'Crediti giornalieri dei primi {count} utenti e di tutti gli altri',
  userSubOne: 'Crediti giornalieri per servizio di {name}',
  everyoneElse: 'Tutti gli altri ({count})',
  member: 'Membro',
  usedLimit: 'Usati / limite mensile',
  limitColumn: 'Limite',
  selectedColumn: 'Selezionato',
  you: 'Tu',
  owner: 'Proprietario',
  admin: 'Amministratore',
  memberRole: 'Membro',
  pausedTag: 'In pausa',
  usedNoLimit: '{used} usati · Nessun limite',
  noLimit: 'Nessun limite',
  ofLimit: '/ {limit}',
  setLimit: 'Imposta limite',
  editLimit: 'Modifica limite',
  showSpendFor: 'Mostra il consumo di {name}',
  selectedShowAll: '{name}, selezionato. Mostra di nuovo tutti',
  limitFor: 'Limite mensile di {name}',
  creditsPerMonth: 'crediti/mese',
  limitHelp: 'Scegli quanti crediti {name} può usare ogni mese.',
  limitInvalid: 'Inserisci un numero intero di crediti, almeno 1.',
  limitPausesNow:
    '{name} ha già usato {used} in questo periodo, quindi questo limite mette subito in pausa il suo utilizzo.',
  limitHelpValid:
    '{name} ha usato {used} in questo periodo. Riceverà un avviso a {notice} e il suo utilizzo verrà messo in pausa a {limit}.',
  save: 'Salva',
  saving: 'Salvataggio…',
  removeLimit: 'Rimuovi limite',
  cancel: 'Annulla',
  limitSaved: 'Il limite mensile di {name} è di {limit} crediti.',
  limitRemoved: '{name} ora non ha un limite mensile.',
  limitFailed: 'Non è stato possibile salvare il limite. Riprova.',
  noteLimits:
    'Un membro che raggiunge il proprio limite viene messo in pausa fino al prossimo rinnovo del piano o finché il proprietario non lo aumenta. Riceve un avviso all’80%.',
  noteAdmin:
    'Vedi il consumo di tutti perché sei proprietario o amministratore. I membri vedono solo il proprio.',
  notePersonal: 'Vedi il tuo consumo. Proprietari e amministratori vedono quello di tutti.',
  summaryCredits: 'Crediti',
  summaryShare: 'Quota del consumo aziendale',
  summaryTop: 'Usa di più',
  summaryTopShare: '{percent} dei suoi crediti',
  summaryRoleYou: '{role} · Tu',
  showAllUsers: 'Mostra tutti gli utenti',
  planChanged: 'Il tuo piano è stato aggiornato.',
};

const lv: Copy = {
  eyebrow: 'Uzņēmuma lietojums',
  title: 'Lietojums',
  intro: 'Jūsu uzņēmuma kopīgais kredītu atlikums un tas, kā jūsu komanda to izmanto.',
  introPersonal: 'Jūsu uzņēmuma kopīgais kredītu atlikums un tas, kā jūs to izmantojat.',
  refresh: 'Atsvaidzināt',
  updated: 'Atjaunināts {time}',
  updatedNow: 'Tikko atjaunināts',
  from: 'No',
  to: 'Līdz',
  chooseFirstDay: 'Izvēlieties pirmo dienu',
  chooseLastDay: 'Izvēlieties pēdējo dienu',
  previousMonth: 'Iepriekšējais mēnesis',
  nextMonth: 'Nākamais mēnesis',
  last7: 'Pēdējās 7 dienas',
  last30: 'Pēdējās 30 dienas',
  last90: 'Pēdējās 90 dienas',
  thisPeriod: 'Pašreizējais periods',
  person: 'Persona',
  allUsers: 'Visi lietotāji',
  peopleNoun: 'zero:personas|one:persona|other:personas',
  personMenu: 'Rādīt vienas personas patēriņu',
  loading: 'Ielādē lietojumu…',
  unavailable: 'Lietojums pašlaik nav pieejams.',
  forbidden: 'Jums nav piekļuves šī uzņēmuma lietojumam.',
  tryAgain: 'Mēģināt vēlreiz',
  balanceTitle: 'Kredītu atlikums',
  creditsUnit: 'kredīti',
  paused: 'Lietojums apturēts',
  planPerMonth: '{credits} kredīti mēnesī',
  planRenews: '+{credits} kredīti {date}',
  planSwitches: 'No {date}: {credits} kredīti mēnesī',
  planEnds: 'Plāns beidzas {date}',
  noPlan: 'Plāna vēl nav',
  planPaymentFailed: 'Maksājums neizdevās',
  planEndedOn: 'Beidzās {date}',
  planEnded: 'Beidzies',
  openBilling: 'Atvērt Norēķinus',
  summaryTopShareSelf: '{percent} no jūsu kredītiem',
  discountUntil: '{percent} atlaide līdz {date}',
  discountOngoing: '{percent} atlaide',
  neverExpire: 'Kredītiem nav derīguma termiņa',
  changePlan: 'Mainīt plānu',
  upgradeNow: 'Jaunināt tagad',
  choosePlan: 'Izvēlēties plānu',
  manageOnWeb: 'Plānus pārvalda Poneva tīmekļa versijā.',
  noticePlanOwner:
    'Lietojums ir apturēts, jo uzņēmumam nav aktīva plāna. Izvēlieties plānu, lai atkal izmantotu kredītus.',
  noticePlanMember:
    'Lietojums ir apturēts, jo uzņēmumam nav aktīva plāna. Palūdziet uzņēmuma īpašniekam to izvēlēties.',
  noticeOutOwner:
    'Visi kredīti ir izmantoti. Jauniniet plānu tagad vai gaidiet līdz {date} plkst. {time}, kad plāns pievienos {credits} kredītus.',
  noticeOutMember:
    'Jūsu uzņēmums ir izmantojis visus kredītus. Palūdziet uzņēmuma īpašniekam jaunināt plānu vai gaidiet līdz {date} plkst. {time}, kad plāns pievienos {credits} kredītus.',
  noticeOutNoRenewal: 'Visi kredīti ir izmantoti. Izvēlieties plānu, lai iegūtu vairāk.',
  noticeLimit:
    'Sasniegts jūsu mēneša limits: {limit} kredīti. Atkal varēsiet izmantot kredītus {date} vai agrāk, ja uzņēmuma īpašnieks palielinās jūsu limitu.',
  noticeLimitSoon:
    'Izmantoti {used} no {limit} mēneša kredītiem. Sasniedzot {limit}, lietojums tiks apturēts.',
  unpriced: 'Darbības bez cenas, kas nav iekļautas šajos skaitļos: {count}.',
  balanceAria:
    'Kredītu atlikums no {from} līdz {to}. Pašlaik {balance} kredīti. Izmantojiet bulttaustiņus, lai lasītu katru dienu.',
  chartAria:
    'Kredīti dienā no {from} līdz {to}. Izmantojiet bulttaustiņus, lai lasītu katru dienu.',
  flagRenewed: 'Atjaunots {date}',
  flagNewPlan: 'Jauns plāns {date}',
  flagAdded: 'Pievienots {date}',
  nextRenewal: 'atjaunosies {date}',
  nextNewPlan: 'jauns plāns no {date}',
  today: 'Šodien, {time}',
  soFar: '{day}, līdz šim',
  spent: 'Iztērēti: {credits}',
  spentSoFar: 'Līdz šim iztērēti: {credits}',
  fromNewPlan: '+{credits} no jaunā plāna',
  atRenewal: '+{credits} atjaunojot',
  added: '+{credits} pievienoti',
  balanceValue: '{credits} kredīti',
  creditsInTotal: 'kopā',
  outOfCredits: 'Kredīti beigušies',
  serviceTitle: 'Kredītu patēriņš pa pakalpojumiem',
  serviceSub: 'Visa uzņēmuma kredīti dienā',
  serviceSubPersonal: 'Jūsu kredīti dienā',
  chat: 'Tērzēšana',
  agents: 'Aģenti',
  apps: 'Lietotnes',
  service: 'Pakalpojums',
  credits: 'Kredīti',
  share: 'Daļa',
  avgPerDay: 'Vid. dienā',
  total: 'Kopā',
  empty: 'Šajā periodā kredīti netika izmantoti',
  emptyBody: 'Patēriņš šeit parādīsies, kad jūsu komanda izmantos Tērzēšanu, Aģentus un Lietotnes.',
  userTitle: 'Kredītu patēriņš pa lietotājiem',
  userSub: 'Kredīti dienā aktīvākajiem lietotājiem ({count}) un visiem pārējiem',
  userSubOne: 'Lietotāja {name} kredīti dienā pa pakalpojumiem',
  everyoneElse: 'Visi pārējie ({count})',
  member: 'Dalībnieks',
  usedLimit: 'Izmantots / mēneša limits',
  limitColumn: 'Limits',
  selectedColumn: 'Atlasīts',
  you: 'Jūs',
  owner: 'Īpašnieks',
  admin: 'Administrators',
  memberRole: 'Dalībnieks',
  pausedTag: 'Apturēts',
  usedNoLimit: 'Izmantoti {used} · Bez limita',
  noLimit: 'Bez limita',
  ofLimit: '/ {limit}',
  setLimit: 'Iestatīt limitu',
  editLimit: 'Rediģēt limitu',
  showSpendFor: 'Rādīt lietotāja {name} patēriņu',
  selectedShowAll: 'Atlasīts: {name}. Atkal rādīt visus',
  limitFor: 'Lietotāja {name} mēneša limits',
  creditsPerMonth: 'kredīti mēnesī',
  limitHelp: 'Izvēlieties, cik kredītu {name} var izmantot katru mēnesi.',
  limitInvalid: 'Ievadiet veselu kredītu skaitu, vismaz 1.',
  limitPausesNow:
    'Lietotāja {name} patēriņš šajā periodā jau ir {used}, tāpēc šis limits lietojumu aptur nekavējoties.',
  limitHelpValid:
    'Lietotāja {name} patēriņš šajā periodā ir {used}. Pie {notice} tiks nosūtīts paziņojums, un pie {limit} lietojums tiks apturēts.',
  save: 'Saglabāt',
  saving: 'Saglabā…',
  removeLimit: 'Noņemt limitu',
  cancel: 'Atcelt',
  limitSaved: 'Lietotāja {name} mēneša limits: {limit} kredīti.',
  limitRemoved: 'Lietotājam {name} vairs nav mēneša limita.',
  limitFailed: 'Neizdevās saglabāt limitu. Mēģiniet vēlreiz.',
  noteLimits:
    'Dalībnieks, kurš sasniedz savu limitu, tiek apturēts līdz nākamajai plāna atjaunošanai vai līdz brīdim, kad īpašnieks limitu palielina. Pie 80% dalībnieks saņem paziņojumu.',
  noteAdmin:
    'Jūs redzat visu patēriņu, jo esat īpašnieks vai administrators. Dalībnieki redz tikai savu.',
  notePersonal: 'Jūs redzat savu patēriņu. Īpašnieki un administratori redz visu patēriņu.',
  summaryCredits: 'Kredīti',
  summaryShare: 'Daļa no uzņēmuma patēriņa',
  summaryTop: 'Visvairāk izmanto',
  summaryTopShare: '{percent} no saviem kredītiem',
  summaryRoleYou: '{role} · Jūs',
  showAllUsers: 'Rādīt visus lietotājus',
  planChanged: 'Jūsu plāns ir atjaunināts.',
};

const lt: Copy = {
  eyebrow: 'Įmonės naudojimas',
  title: 'Naudojimas',
  intro: 'Bendras jūsų įmonės kreditų likutis ir kaip jį naudoja jūsų komanda.',
  introPersonal: 'Bendras jūsų įmonės kreditų likutis ir kaip jį naudojate jūs.',
  refresh: 'Atnaujinti',
  updated: 'Atnaujinta {time}',
  updatedNow: 'Ką tik atnaujinta',
  from: 'Nuo',
  to: 'Iki',
  chooseFirstDay: 'Pasirinkite pirmą dieną',
  chooseLastDay: 'Pasirinkite paskutinę dieną',
  previousMonth: 'Ankstesnis mėnuo',
  nextMonth: 'Kitas mėnuo',
  last7: 'Paskutinės 7 dienos',
  last30: 'Paskutinės 30 dienų',
  last90: 'Paskutinės 90 dienų',
  thisPeriod: 'Šis laikotarpis',
  person: 'Asmuo',
  allUsers: 'Visi naudotojai',
  peopleNoun: 'one:asmuo|few:asmenys|many:asmens|other:asmenų',
  personMenu: 'Rodyti vieno asmens sąnaudas',
  loading: 'Įkeliamas naudojimas…',
  unavailable: 'Naudojimo duomenys šiuo metu nepasiekiami.',
  forbidden: 'Neturite prieigos prie šios įmonės naudojimo duomenų.',
  tryAgain: 'Bandyti dar kartą',
  balanceTitle: 'Kreditų likutis',
  creditsUnit: 'kreditų',
  paused: 'Naudojimas pristabdytas',
  planPerMonth: '{credits} kreditų per mėnesį',
  planRenews: '+{credits} kreditų {date}',
  planSwitches: 'Nuo {date}: {credits} kreditų per mėnesį',
  planEnds: 'Planas baigiasi {date}',
  noPlan: 'Plano dar nėra',
  planPaymentFailed: 'Mokėjimas nepavyko',
  planEndedOn: 'Baigėsi {date}',
  planEnded: 'Pasibaigęs',
  openBilling: 'Eiti į Atsiskaitymus',
  summaryTopShareSelf: '{percent} jūsų kreditų',
  discountUntil: '{percent} nuolaida iki {date}',
  discountOngoing: '{percent} nuolaida',
  neverExpire: 'Kreditų galiojimas nesibaigia',
  changePlan: 'Keisti planą',
  upgradeNow: 'Padidinti planą',
  choosePlan: 'Pasirinkti planą',
  manageOnWeb: 'Planai valdomi Poneva žiniatinklio versijoje.',
  noticePlanOwner:
    'Naudojimas pristabdytas, nes įmonė neturi aktyvaus plano. Pasirinkite planą, kad vėl galėtumėte naudoti kreditus.',
  noticePlanMember:
    'Naudojimas pristabdytas, nes įmonė neturi aktyvaus plano. Paprašykite įmonės savininko jį pasirinkti.',
  noticeOutOwner:
    'Išnaudojote visus kreditus. Padidinkite planą dabar arba palaukite iki {date} {time}, kai planas pridės {credits} kreditų.',
  noticeOutMember:
    'Jūsų įmonė išnaudojo visus kreditus. Paprašykite įmonės savininko padidinti planą arba palaukite iki {date} {time}, kai planas pridės {credits} kreditų.',
  noticeOutNoRenewal: 'Išnaudojote visus kreditus. Pasirinkite planą, kad gautumėte daugiau.',
  noticeLimit:
    'Pasiekėte savo mėnesio limitą ({limit} kreditų). Kreditus vėl galėsite naudoti {date} arba anksčiau, jei įmonės savininkas padidins jūsų limitą.',
  noticeLimitSoon:
    'Panaudojote {used} iš {limit} mėnesio kreditų. Pasiekus {limit}, naudojimas bus pristabdytas.',
  unpriced: 'Veiksmų, kurie dar neturi kainos ir neįtraukti į šiuos skaičius: {count}.',
  balanceAria:
    'Kreditų likutis nuo {from} iki {to}. Dabartinis likutis: {balance}. Naudokite rodyklių klavišus, kad perskaitytumėte kiekvieną dieną.',
  chartAria:
    'Kreditai per dieną nuo {from} iki {to}. Naudokite rodyklių klavišus, kad perskaitytumėte kiekvieną dieną.',
  flagRenewed: 'Atnaujinta {date}',
  flagNewPlan: 'Naujas planas {date}',
  flagAdded: 'Pridėta {date}',
  nextRenewal: 'atsinaujins {date}',
  nextNewPlan: 'naujas planas nuo {date}',
  today: 'Šiandien, {time}',
  soFar: '{day}, kol kas',
  spent: 'Išleista: {credits}',
  spentSoFar: 'Kol kas išleista: {credits}',
  fromNewPlan: '+{credits} iš naujo plano',
  atRenewal: '+{credits} atsinaujinus',
  added: 'Pridėta +{credits}',
  balanceValue: 'Likutis: {credits}',
  creditsInTotal: 'iš viso',
  outOfCredits: 'Kreditai baigėsi',
  serviceTitle: 'Kreditų sąnaudos pagal paslaugą',
  serviceSub: 'Visos įmonės kreditai per dieną',
  serviceSubPersonal: 'Jūsų kreditai per dieną',
  chat: 'Pokalbiai',
  agents: 'Agentai',
  apps: 'Programėlės',
  service: 'Paslauga',
  credits: 'Kreditai',
  share: 'Dalis',
  avgPerDay: 'Vid. per dieną',
  total: 'Iš viso',
  empty: 'Šiuo laikotarpiu kreditai nenaudoti',
  emptyBody: 'Sąnaudos bus rodomos čia, kai jūsų komanda naudos Pokalbius, Agentus ir Programėles.',
  userTitle: 'Kreditų sąnaudos pagal naudotoją',
  userSub: 'Daugiausia naudojančių ({count}) ir visų kitų kreditai per dieną',
  userSubOne: 'Naudotojo {name} kreditai per dieną pagal paslaugą',
  everyoneElse: 'Visi kiti ({count})',
  member: 'Narys',
  usedLimit: 'Panaudota / mėnesio limitas',
  limitColumn: 'Limitas',
  selectedColumn: 'Pasirinkta',
  you: 'Jūs',
  owner: 'Savininkas',
  admin: 'Administratorius',
  memberRole: 'Narys',
  pausedTag: 'Pristabdyta',
  usedNoLimit: 'Panaudota {used} · Be limito',
  noLimit: 'Be limito',
  ofLimit: '/ {limit}',
  setLimit: 'Nustatyti limitą',
  editLimit: 'Keisti limitą',
  showSpendFor: 'Rodyti naudotojo {name} sąnaudas',
  selectedShowAll: 'Pasirinkta: {name}. Vėl rodyti visus',
  limitFor: 'Naudotojo {name} mėnesio limitas',
  creditsPerMonth: 'kreditų per mėnesį',
  limitHelp: 'Pasirinkite, kiek kreditų {name} gali naudoti kas mėnesį.',
  limitInvalid: 'Įveskite sveikąjį kreditų skaičių, ne mažiau kaip 1.',
  limitPausesNow:
    'Naudotojo {name} sąnaudos per šį laikotarpį jau siekia {used}, todėl šis limitas naudojimą pristabdys iš karto.',
  limitHelpValid:
    'Naudotojo {name} sąnaudos per šį laikotarpį: {used}. Pasiekus {notice}, bus išsiųstas pranešimas, o pasiekus {limit}, naudojimas bus pristabdytas.',
  save: 'Išsaugoti',
  saving: 'Išsaugoma…',
  removeLimit: 'Pašalinti limitą',
  cancel: 'Atšaukti',
  limitSaved: 'Naudotojo {name} mėnesio limitas nustatytas: {limit}.',
  limitRemoved: 'Naudotojas {name} nebeturi mėnesio limito.',
  limitFailed: 'Nepavyko išsaugoti limito. Bandykite dar kartą.',
  noteLimits:
    'Narys, pasiekęs savo limitą, pristabdomas iki kito plano atsinaujinimo arba kol savininkas padidins limitą. Pasiekus 80 %, narys gauna pranešimą.',
  noteAdmin:
    'Matote visų sąnaudas, nes esate savininkas arba administratorius. Nariai mato tik savo.',
  notePersonal: 'Matote savo sąnaudas. Savininkai ir administratoriai mato visų sąnaudas.',
  summaryCredits: 'Kreditai',
  summaryShare: 'Dalis įmonės sąnaudų',
  summaryTop: 'Daugiausia naudoja',
  summaryTopShare: '{percent} savo kreditų',
  summaryRoleYou: '{role} · Jūs',
  showAllUsers: 'Rodyti visus naudotojus',
  planChanged: 'Jūsų planas atnaujintas.',
};

const mt: Copy = {
  eyebrow: 'Użu tal-kumpanija',
  title: 'Użu',
  intro: 'Il-bilanċ kondiviż tal-krediti tal-kumpanija tiegħek, u kif it-tim tiegħek qed jużah.',
  introPersonal: 'Il-bilanċ kondiviż tal-krediti tal-kumpanija tiegħek, u kif qed tużah int.',
  refresh: 'Aġġorna',
  updated: 'Aġġornat: {time}',
  updatedNow: 'Għadu kemm ġie aġġornat',
  from: 'Minn',
  to: 'Sa',
  chooseFirstDay: 'Agħżel l-ewwel jum',
  chooseLastDay: 'Agħżel l-aħħar jum',
  previousMonth: 'Ix-xahar ta’ qabel',
  nextMonth: 'Ix-xahar ta’ wara',
  last7: 'L-aħħar 7 ijiem',
  last30: 'L-aħħar 30 jum',
  last90: 'L-aħħar 90 jum',
  thisPeriod: 'Il-perjodu attwali',
  person: 'Persuna',
  allUsers: 'L-utenti kollha',
  peopleNoun: 'one:persuna|two:persuni|few:persuni|many:persuna|other:persuna',
  personMenu: 'Uri l-infiq ta’ persuna waħda',
  loading: 'Qed jitgħabba l-użu…',
  unavailable: 'L-użu mhuwiex disponibbli bħalissa.',
  forbidden: 'M’għandekx aċċess għall-użu ta’ din il-kumpanija.',
  tryAgain: 'Erġa’ pprova',
  balanceTitle: 'Bilanċ tal-krediti',
  creditsUnit: 'krediti',
  paused: 'Użu sospiż',
  planPerMonth: '{credits} krediti fix-xahar',
  planRenews: '+{credits} krediti nhar {date}',
  planSwitches: 'Jaqleb għal {credits} krediti fix-xahar nhar {date}',
  planEnds: 'Il-pjan jispiċċa nhar {date}',
  noPlan: 'Għad m’hemmx pjan',
  planPaymentFailed: 'Il-ħlas ma rnexxiex',
  planEndedOn: 'Intemm nhar {date}',
  planEnded: 'Intemm',
  openBilling: 'Mur għal Ħlas',
  summaryTopShareSelf: '{percent} tal-krediti tiegħek',
  discountUntil: '{percent} skont sa {date}',
  discountOngoing: '{percent} skont',
  neverExpire: 'Il-krediti qatt ma jiskadu',
  changePlan: 'Ibdel il-pjan',
  upgradeNow: 'Itla’ għal pjan akbar',
  choosePlan: 'Agħżel pjan',
  manageOnWeb: 'Il-pjanijiet jiġu ġestiti f’Poneva fuq il-web.',
  noticePlanOwner:
    'L-użu huwa sospiż għax il-kumpanija m’għandhiex pjan attiv. Agħżel pjan biex terġa’ tuża l-krediti.',
  noticePlanMember:
    'L-użu huwa sospiż għax il-kumpanija m’għandhiex pjan attiv. Itlob lis-sid tal-kumpanija jagħżel wieħed.',
  noticeOutOwner:
    'Użajt il-krediti kollha tiegħek. Itla’ għal pjan akbar issa, jew stenna sa {date}, {time}, meta l-pjan tiegħek iżid {credits} krediti.',
  noticeOutMember:
    'Il-kumpanija tiegħek użat il-krediti kollha tagħha. Itlob lis-sid tal-kumpanija jtella’ l-pjan, jew stenna sa {date}, {time}, meta l-pjan iżid {credits} krediti.',
  noticeOutNoRenewal: 'Użajt il-krediti kollha tiegħek. Agħżel pjan biex ikollok aktar.',
  noticeLimit:
    'Wasalt għal-limitu ta’ kull xahar ta’ {limit} krediti. Tista’ terġa’ tuża l-krediti nhar {date}, jew qabel jekk is-sid tal-kumpanija jgħolli l-limitu tiegħek.',
  noticeLimitSoon:
    'Użajt {used} minn {limit} krediti ta’ kull xahar. L-użu jiġi sospiż meta tilħaq {limit}.',
  unpriced: 'Azzjonijiet li għad m’għandhomx prezz u mhumiex inklużi f’dawn iċ-ċifri: {count}.',
  balanceAria:
    'Bilanċ tal-krediti minn {from} sa {to}. Issa {balance} krediti. Uża t-tasti tal-vleġeġ biex taqra kull jum.',
  chartAria: 'Krediti kuljum minn {from} sa {to}. Uża t-tasti tal-vleġeġ biex taqra kull jum.',
  flagRenewed: 'Imġedded {date}',
  flagNewPlan: 'Pjan ġdid {date}',
  flagAdded: 'Miżjud {date}',
  nextRenewal: 'nhar {date}',
  nextNewPlan: 'pjan ġdid minn {date}',
  today: 'Illum, {time}',
  soFar: '{day}, s’issa',
  spent: '{credits} minfuqa',
  spentSoFar: '{credits} minfuqa s’issa',
  fromNewPlan: '+{credits} mill-pjan il-ġdid',
  atRenewal: '+{credits} mat-tiġdid',
  added: '+{credits} miżjuda',
  balanceValue: '{credits} krediti',
  creditsInTotal: 'krediti b’kollox',
  outOfCredits: 'Spiċċaw il-krediti',
  serviceTitle: 'Infiq tal-krediti skont is-servizz',
  serviceSub: 'Krediti kuljum għall-kumpanija kollha',
  serviceSubPersonal: 'Il-krediti tiegħek kuljum',
  chat: 'Chat',
  agents: 'Aġenti',
  apps: 'Apps',
  service: 'Servizz',
  credits: 'Krediti',
  share: 'Sehem',
  avgPerDay: 'Medja kuljum',
  total: 'Total',
  empty: 'Ma ntużaw l-ebda krediti f’dan il-perjodu',
  emptyBody: 'L-infiq jidher hawn meta t-tim tiegħek juża Chat, Aġenti u Apps.',
  userTitle: 'Infiq tal-krediti skont l-utent',
  userSub: 'Krediti kuljum għall-utenti l-aktar attivi ({count}) u għal kulħadd l-ieħor',
  userSubOne: 'Krediti kuljum skont is-servizz għal {name}',
  everyoneElse: 'Kulħadd l-ieħor ({count})',
  member: 'Membru',
  usedLimit: 'Użat / limitu ta’ kull xahar',
  limitColumn: 'Limitu',
  selectedColumn: 'Magħżul',
  you: 'Int',
  owner: 'Sid',
  admin: 'Amministratur',
  memberRole: 'Membru',
  pausedTag: 'Sospiż',
  usedNoLimit: '{used} użati · L-ebda limitu',
  noLimit: 'L-ebda limitu',
  ofLimit: '/ {limit}',
  setLimit: 'Issettja limitu',
  editLimit: 'Editja l-limitu',
  showSpendFor: 'Uri l-infiq ta’ {name}',
  selectedShowAll: '{name}, magħżul. Erġa’ uri lil kulħadd',
  limitFor: 'Limitu ta’ kull xahar għal {name}',
  creditsPerMonth: 'krediti fix-xahar',
  limitHelp: 'Agħżel kemm krediti jista’ juża {name} kull xahar.',
  limitInvalid: 'Daħħal numru sħiħ ta’ krediti, 1 jew aktar.',
  limitPausesNow:
    '{name} diġà uża {used} f’dan il-perjodu, għalhekk dan il-limitu jissospendi l-użu minnufih.',
  limitHelpValid:
    '{name} uża {used} f’dan il-perjodu. Jasal avviż meta jintlaħaq {notice} u l-użu jiġi sospiż meta jintlaħaq {limit}.',
  save: 'Issejvja',
  saving: 'Qed jissejvja…',
  removeLimit: 'Neħħi l-limitu',
  cancel: 'Ikkanċella',
  limitSaved: 'Il-limitu ta’ kull xahar għal {name} huwa {limit} krediti.',
  limitRemoved: '{name} m’għandux limitu ta’ kull xahar issa.',
  limitFailed: 'Ma rnexxilniex nissejvjaw il-limitu. Erġa’ pprova.',
  noteLimits:
    'Membru li jilħaq il-limitu tiegħu jiġi sospiż sat-tiġdid li jmiss tal-pjan jew sakemm is-sid jgħollih. Jirċievi avviż meta jilħaq 80%.',
  noteAdmin:
    'Tara l-infiq ta’ kulħadd għax int sid jew amministratur. Il-membri jaraw biss tagħhom.',
  notePersonal: 'Tara l-infiq tiegħek. Is-sidien u l-amministraturi jaraw dak ta’ kulħadd.',
  summaryCredits: 'Krediti',
  summaryShare: 'Sehem mill-infiq tal-kumpanija',
  summaryTop: 'L-aktar użat',
  summaryTopShare: '{percent} tal-krediti tiegħu',
  summaryRoleYou: '{role} · Int',
  showAllUsers: 'Uri l-utenti kollha',
  planChanged: 'Il-pjan tiegħek ġie aġġornat.',
};

const pl: Copy = {
  eyebrow: 'Wykorzystanie w firmie',
  title: 'Wykorzystanie',
  intro: 'Wspólne saldo kredytów Twojej firmy i to, jak korzysta z niego Twój zespół.',
  introPersonal: 'Wspólne saldo kredytów Twojej firmy i to, jak z niego korzystasz.',
  refresh: 'Odśwież',
  updated: 'Zaktualizowano o {time}',
  updatedNow: 'Zaktualizowano przed chwilą',
  from: 'Od',
  to: 'Do',
  chooseFirstDay: 'Wybierz pierwszy dzień',
  chooseLastDay: 'Wybierz ostatni dzień',
  previousMonth: 'Poprzedni miesiąc',
  nextMonth: 'Następny miesiąc',
  last7: 'Ostatnie 7 dni',
  last30: 'Ostatnie 30 dni',
  last90: 'Ostatnie 90 dni',
  thisPeriod: 'Bieżący okres',
  person: 'Osoba',
  allUsers: 'Wszyscy użytkownicy',
  peopleNoun: 'one:osoba|few:osoby|many:osób|other:osoby',
  personMenu: 'Pokaż zużycie jednej osoby',
  loading: 'Wczytywanie wykorzystania…',
  unavailable: 'Dane o wykorzystaniu są teraz niedostępne.',
  forbidden: 'Nie masz dostępu do danych o wykorzystaniu tej firmy.',
  tryAgain: 'Spróbuj ponownie',
  balanceTitle: 'Saldo kredytów',
  creditsUnit: 'kredytów',
  paused: 'Korzystanie wstrzymane',
  planPerMonth: '{credits} kredytów/mies.',
  planRenews: '+{credits} kredytów {date}',
  planSwitches: 'Od {date}: {credits} kredytów/mies.',
  planEnds: 'Plan kończy się {date}',
  noPlan: 'Brak planu',
  planPaymentFailed: 'Płatność nie powiodła się',
  planEndedOn: 'Zakończony {date}',
  planEnded: 'Zakończony',
  openBilling: 'Przejdź do Płatności',
  summaryTopShareSelf: '{percent} Twoich kredytów',
  discountUntil: '{percent} zniżki do {date}',
  discountOngoing: '{percent} zniżki',
  neverExpire: 'Kredyty nigdy nie wygasają',
  changePlan: 'Zmień plan',
  upgradeNow: 'Wybierz większy plan',
  choosePlan: 'Wybierz plan',
  manageOnWeb: 'Planami zarządza się w przeglądarkowej wersji Poneva.',
  noticePlanOwner:
    'Korzystanie jest wstrzymane, ponieważ firma nie ma aktywnego planu. Wybierz plan, aby znów korzystać z kredytów.',
  noticePlanMember:
    'Korzystanie jest wstrzymane, ponieważ firma nie ma aktywnego planu. Poproś właściciela firmy o wybranie planu.',
  noticeOutOwner:
    'Wykorzystano wszystkie kredyty. Wybierz teraz większy plan albo poczekaj do {date}, godz. {time}, gdy plan doda {credits} kredytów.',
  noticeOutMember:
    'Twoja firma wykorzystała wszystkie kredyty. Poproś właściciela firmy o większy plan albo poczekaj do {date}, godz. {time}, gdy plan doda {credits} kredytów.',
  noticeOutNoRenewal: 'Wykorzystano wszystkie kredyty. Wybierz plan, aby otrzymać więcej.',
  noticeLimit:
    'Osiągnięto Twój miesięczny limit ({limit} kredytów). Możesz znów korzystać z kredytów od {date} lub wcześniej, jeśli właściciel firmy podniesie Twój limit.',
  noticeLimitSoon:
    'Wykorzystano {used} z {limit} kredytów na ten miesiąc. Po osiągnięciu {limit} korzystanie zostanie wstrzymane.',
  unpriced: 'Liczba działań bez ustalonej ceny, pominiętych w tych danych: {count}.',
  balanceAria:
    'Saldo kredytów od {from} do {to}. Obecne saldo: {balance}. Użyj klawiszy strzałek, aby odczytać kolejne dni.',
  chartAria:
    'Kredyty dziennie od {from} do {to}. Użyj klawiszy strzałek, aby odczytać kolejne dni.',
  flagRenewed: 'Odnowiono {date}',
  flagNewPlan: 'Nowy plan {date}',
  flagAdded: 'Dodano {date}',
  nextRenewal: 'odnowienie {date}',
  nextNewPlan: 'nowy plan od {date}',
  today: 'Dziś, {time}',
  soFar: '{day}, dotychczas',
  spent: 'Zużyto {credits}',
  spentSoFar: 'Dotychczas zużyto {credits}',
  fromNewPlan: '+{credits} z nowego planu',
  atRenewal: '+{credits} przy odnowieniu',
  added: 'Dodano +{credits}',
  balanceValue: 'Saldo: {credits}',
  creditsInTotal: 'łącznie',
  outOfCredits: 'Brak kredytów',
  serviceTitle: 'Zużycie kredytów według usług',
  serviceSub: 'Dzienne kredyty całej firmy',
  serviceSubPersonal: 'Twoje dzienne kredyty',
  chat: 'Czat',
  agents: 'Agenci',
  apps: 'Aplikacje',
  service: 'Usługa',
  credits: 'Kredyty',
  share: 'Udział',
  avgPerDay: 'Śr. dziennie',
  total: 'Razem',
  empty: 'W tym okresie nie użyto kredytów',
  emptyBody:
    'Zużycie pojawi się tutaj, gdy Twój zespół zacznie korzystać z Czatu, Agentów i Aplikacji.',
  userTitle: 'Zużycie kredytów według użytkowników',
  userSub: 'Dzienne kredyty najaktywniejszych osób ({count}) i wszystkich pozostałych',
  userSubOne: 'Dzienne kredyty według usług: {name}',
  everyoneElse: 'Wszyscy pozostali ({count})',
  member: 'Członek',
  usedLimit: 'Wykorzystano / limit miesięczny',
  limitColumn: 'Limit',
  selectedColumn: 'Wybrano',
  you: 'Ty',
  owner: 'Właściciel',
  admin: 'Administrator',
  memberRole: 'Członek',
  pausedTag: 'Wstrzymano',
  usedNoLimit: 'Wykorzystano {used} · Bez limitu',
  noLimit: 'Bez limitu',
  ofLimit: '/ {limit}',
  setLimit: 'Ustaw limit',
  editLimit: 'Edytuj limit',
  showSpendFor: 'Pokaż zużycie: {name}',
  selectedShowAll: 'Wybrano: {name}. Pokaż ponownie wszystkich',
  limitFor: 'Limit miesięczny: {name}',
  creditsPerMonth: 'kredytów/mies.',
  limitHelp: 'Wybierz, ile kredytów {name} może wykorzystać w miesiącu.',
  limitInvalid: 'Wpisz liczbę całkowitą kredytów, co najmniej 1.',
  limitPausesNow:
    'W tym okresie {name} ma już wykorzystane {used}, więc ten limit od razu wstrzyma korzystanie.',
  limitHelpValid:
    'W tym okresie {name} ma wykorzystane {used}. Powiadomienie pojawi się przy {notice}, a korzystanie zostanie wstrzymane przy {limit}.',
  save: 'Zapisz',
  saving: 'Zapisywanie…',
  removeLimit: 'Usuń limit',
  cancel: 'Anuluj',
  limitSaved: 'Limit miesięczny ({name}): {limit}.',
  limitRemoved: 'Usunięto limit miesięczny ({name}).',
  limitFailed: 'Nie udało się zapisać limitu. Spróbuj ponownie.',
  noteLimits:
    'Członek, który osiągnie swój limit, zostaje wstrzymany do następnego odnowienia planu lub do czasu, aż właściciel podniesie limit. Przy 80% otrzymuje powiadomienie.',
  noteAdmin:
    'Widzisz zużycie wszystkich, ponieważ jesteś właścicielem lub administratorem. Członkowie widzą tylko własne.',
  notePersonal: 'Widzisz własne zużycie. Właściciele i administratorzy widzą zużycie wszystkich.',
  summaryCredits: 'Kredyty',
  summaryShare: 'Udział w zużyciu firmy',
  summaryTop: 'Najwięcej używa',
  summaryTopShare: '{percent} własnych kredytów',
  summaryRoleYou: '{role} · Ty',
  showAllUsers: 'Pokaż wszystkich użytkowników',
  planChanged: 'Twój plan został zaktualizowany.',
};

const pt: Copy = {
  eyebrow: 'Utilização da empresa',
  title: 'Utilização',
  intro: 'O saldo de créditos partilhado da sua empresa e como a sua equipa o utiliza.',
  introPersonal: 'O saldo de créditos partilhado da sua empresa e como o utiliza.',
  refresh: 'Atualizar',
  updated: 'Atualizado às {time}',
  updatedNow: 'Atualizado agora mesmo',
  from: 'De',
  to: 'Até',
  chooseFirstDay: 'Escolher o primeiro dia',
  chooseLastDay: 'Escolher o último dia',
  previousMonth: 'Mês anterior',
  nextMonth: 'Mês seguinte',
  last7: 'Últimos 7 dias',
  last30: 'Últimos 30 dias',
  last90: 'Últimos 90 dias',
  thisPeriod: 'Período atual',
  person: 'Pessoa',
  allUsers: 'Todos os utilizadores',
  peopleNoun: 'one:pessoa|many:de pessoas|other:pessoas',
  personMenu: 'Mostrar o consumo de uma pessoa',
  loading: 'A carregar a utilização…',
  unavailable: 'A utilização não está disponível neste momento.',
  forbidden: 'Não tem acesso à utilização desta empresa.',
  tryAgain: 'Tentar novamente',
  balanceTitle: 'Saldo de créditos',
  creditsUnit: 'créditos',
  paused: 'Utilização em pausa',
  planPerMonth: '{credits} créditos/mês',
  planRenews: '+{credits} créditos a {date}',
  planSwitches: 'Muda para {credits} créditos/mês a {date}',
  planEnds: 'O plano termina a {date}',
  noPlan: 'Ainda sem plano',
  planPaymentFailed: 'Pagamento falhou',
  planEndedOn: 'Terminou a {date}',
  planEnded: 'Terminado',
  openBilling: 'Ir para Faturação',
  summaryTopShareSelf: '{percent} dos seus próprios créditos',
  discountUntil: '{percent} de desconto até {date}',
  discountOngoing: '{percent} de desconto',
  neverExpire: 'Os créditos nunca expiram',
  changePlan: 'Mudar de plano',
  upgradeNow: 'Fazer upgrade agora',
  choosePlan: 'Escolher um plano',
  manageOnWeb: 'Os planos são geridos na versão web do Poneva.',
  noticePlanOwner:
    'A utilização está em pausa porque a empresa não tem um plano ativo. Escolha um plano para voltar a utilizar créditos.',
  noticePlanMember:
    'A utilização está em pausa porque a empresa não tem um plano ativo. Peça ao proprietário da empresa que escolha um.',
  noticeOutOwner:
    'Utilizou todos os seus créditos. Faça upgrade agora ou aguarde até {date} às {time}, quando o seu plano adicionar {credits} créditos.',
  noticeOutMember:
    'A sua empresa utilizou todos os créditos. Peça ao proprietário da empresa que faça upgrade ou aguarde até {date} às {time}, quando o plano adicionar {credits} créditos.',
  noticeOutNoRenewal: 'Utilizou todos os seus créditos. Escolha um plano para obter mais.',
  noticeLimit:
    'Atingiu o seu limite mensal de {limit} créditos. Poderá voltar a utilizar créditos a {date}, ou antes, se o proprietário da empresa aumentar o seu limite.',
  noticeLimitSoon:
    'Utilizou {used} dos seus {limit} créditos mensais. A sua utilização fica em pausa quando atingir {limit}.',
  unpriced: '{count} ações ainda não têm preço e ficam de fora destes números.',
  balanceAria:
    'Saldo de créditos de {from} a {to}. Agora {balance} créditos. Utilize as teclas de seta para percorrer os dias.',
  chartAria: 'Créditos diários de {from} a {to}. Utilize as teclas de seta para percorrer os dias.',
  flagRenewed: 'Renovado a {date}',
  flagNewPlan: 'Novo plano a {date}',
  flagAdded: 'Adicionados a {date}',
  nextRenewal: 'a {date}',
  nextNewPlan: 'novo plano a partir de {date}',
  today: 'Hoje, {time}',
  soFar: '{day}, até agora',
  spent: '{credits} gastos',
  spentSoFar: '{credits} gastos até agora',
  fromNewPlan: '+{credits} do novo plano',
  atRenewal: '+{credits} na renovação',
  added: '+{credits} adicionados',
  balanceValue: '{credits} créditos',
  creditsInTotal: 'créditos no total',
  outOfCredits: 'Sem créditos',
  serviceTitle: 'Consumo de créditos por serviço',
  serviceSub: 'Créditos diários de toda a empresa',
  serviceSubPersonal: 'Os seus créditos diários',
  chat: 'Chat',
  agents: 'Agentes',
  apps: 'Apps',
  service: 'Serviço',
  credits: 'Créditos',
  share: 'Percentagem',
  avgPerDay: 'Média diária',
  total: 'Total',
  empty: 'Nenhum crédito utilizado neste período',
  emptyBody: 'O consumo aparece aqui quando a sua equipa utiliza Chat, Agentes e Apps.',
  userTitle: 'Consumo de créditos por utilizador',
  userSub: 'Créditos diários dos {count} utilizadores com mais consumo e dos restantes',
  userSubOne: 'Créditos diários por serviço de {name}',
  everyoneElse: 'Restantes ({count})',
  member: 'Membro',
  usedLimit: 'Utilizado / limite mensal',
  limitColumn: 'Limite',
  selectedColumn: 'Selecionado',
  you: 'Você',
  owner: 'Proprietário',
  admin: 'Administrador',
  memberRole: 'Membro',
  pausedTag: 'Em pausa',
  usedNoLimit: '{used} utilizados · Sem limite',
  noLimit: 'Sem limite',
  ofLimit: '/ {limit}',
  setLimit: 'Definir limite',
  editLimit: 'Editar limite',
  showSpendFor: 'Mostrar o consumo de {name}',
  selectedShowAll: '{name}, selecionado. Mostrar todos novamente',
  limitFor: 'Limite mensal de {name}',
  creditsPerMonth: 'créditos/mês',
  limitHelp: 'Escolha quantos créditos {name} pode utilizar por mês.',
  limitInvalid: 'Introduza um número inteiro de créditos, 1 ou mais.',
  limitPausesNow:
    '{name} já utilizou {used} neste período, por isso este limite coloca a utilização em pausa de imediato.',
  limitHelpValid:
    '{name} utilizou {used} neste período. Recebe um aviso aos {notice} e a utilização fica em pausa aos {limit}.',
  save: 'Guardar',
  saving: 'A guardar…',
  removeLimit: 'Remover limite',
  cancel: 'Cancelar',
  limitSaved: 'O limite mensal de {name} é de {limit} créditos.',
  limitRemoved: '{name} já não tem limite mensal.',
  limitFailed: 'Não foi possível guardar o limite. Tente novamente.',
  noteLimits:
    'Um membro que atinja o seu limite fica em pausa até à próxima renovação do plano ou até o proprietário aumentar o limite. Recebe um aviso aos 80%.',
  noteAdmin:
    'Vê o consumo de todos porque é proprietário ou administrador. Os membros só veem o seu.',
  notePersonal: 'Vê o seu próprio consumo. Os proprietários e administradores veem o de todos.',
  summaryCredits: 'Créditos',
  summaryShare: 'Parte do consumo da empresa',
  summaryTop: 'Utiliza mais',
  summaryTopShare: '{percent} dos seus créditos',
  summaryRoleYou: '{role} · Você',
  showAllUsers: 'Mostrar todos os utilizadores',
  planChanged: 'O seu plano foi atualizado.',
};

const ro: Copy = {
  eyebrow: 'Utilizarea în companie',
  title: 'Utilizare',
  intro: 'Soldul comun de credite al companiei dvs. și modul în care îl folosește echipa dvs.',
  introPersonal: 'Soldul comun de credite al companiei dvs. și modul în care îl folosiți.',
  refresh: 'Reîmprospătați',
  updated: 'Actualizat la {time}',
  updatedNow: 'Actualizat chiar acum',
  from: 'De la',
  to: 'Până la',
  chooseFirstDay: 'Alegeți prima zi',
  chooseLastDay: 'Alegeți ultima zi',
  previousMonth: 'Luna anterioară',
  nextMonth: 'Luna următoare',
  last7: 'Ultimele 7 zile',
  last30: 'Ultimele 30 de zile',
  last90: 'Ultimele 90 de zile',
  thisPeriod: 'Perioada curentă',
  person: 'Persoană',
  allUsers: 'Toți utilizatorii',
  peopleNoun: 'one:persoană|few:persoane|other:de persoane',
  personMenu: 'Afișați consumul unei persoane',
  loading: 'Se încarcă utilizarea…',
  unavailable: 'Utilizarea nu este disponibilă momentan.',
  forbidden: 'Nu aveți acces la utilizarea acestei companii.',
  tryAgain: 'Încercați din nou',
  balanceTitle: 'Sold de credite',
  creditsUnit: 'credite',
  paused: 'Utilizare suspendată',
  planPerMonth: '{credits} de credite/lună',
  planRenews: '+{credits} de credite pe {date}',
  planSwitches: 'Trece la {credits} de credite/lună pe {date}',
  planEnds: 'Planul se încheie pe {date}',
  noPlan: 'Niciun plan încă',
  planPaymentFailed: 'Plata a eșuat',
  planEndedOn: 'S-a încheiat pe {date}',
  planEnded: 'Încheiat',
  openBilling: 'Mergeți la Facturare',
  summaryTopShareSelf: '{percent} din creditele dvs.',
  discountUntil: 'Reducere de {percent} până pe {date}',
  discountOngoing: 'Reducere de {percent}',
  neverExpire: 'Creditele nu expiră niciodată',
  changePlan: 'Schimbați planul',
  upgradeNow: 'Treceți la un plan superior',
  choosePlan: 'Alegeți un plan',
  manageOnWeb: 'Planurile se gestionează în versiunea web Poneva.',
  noticePlanOwner:
    'Utilizarea este suspendată deoarece compania nu are un plan activ. Alegeți un plan pentru a folosi din nou credite.',
  noticePlanMember:
    'Utilizarea este suspendată deoarece compania nu are un plan activ. Cereți proprietarului companiei să aleagă unul.',
  noticeOutOwner:
    'Ați folosit toate creditele. Treceți acum la un plan superior sau așteptați până pe {date} la ora {time}, când planul adaugă {credits} de credite.',
  noticeOutMember:
    'Compania dvs. a folosit toate creditele. Cereți proprietarului companiei să treacă la un plan superior sau așteptați până pe {date} la ora {time}, când planul adaugă {credits} de credite.',
  noticeOutNoRenewal: 'Ați folosit toate creditele. Alegeți un plan pentru a obține mai multe.',
  noticeLimit:
    'Ați atins limita lunară de {limit} de credite. Puteți folosi din nou credite pe {date} sau mai devreme, dacă proprietarul companiei vă mărește limita.',
  noticeLimitSoon:
    'Ați folosit {used} din limita lunară de {limit}. Utilizarea se suspendă când ajungeți la {limit}.',
  unpriced: 'Acțiuni care nu au încă preț și nu sunt incluse în aceste cifre: {count}.',
  balanceAria:
    'Soldul de credite de la {from} până la {to}. Sold actual: {balance}. Folosiți tastele săgeți pentru a parcurge zilele.',
  chartAria:
    'Credite pe zi de la {from} până la {to}. Folosiți tastele săgeți pentru a parcurge zilele.',
  flagRenewed: 'Reînnoit pe {date}',
  flagNewPlan: 'Plan nou pe {date}',
  flagAdded: 'Adăugate pe {date}',
  nextRenewal: 'pe {date}',
  nextNewPlan: 'plan nou de pe {date}',
  today: 'Azi, {time}',
  soFar: '{day}, până acum',
  spent: 'Consumate: {credits}',
  spentSoFar: 'Consumate până acum: {credits}',
  fromNewPlan: '+{credits} din planul nou',
  atRenewal: '+{credits} la reînnoire',
  added: '+{credits} adăugate',
  balanceValue: 'Sold: {credits}',
  creditsInTotal: 'credite în total',
  outOfCredits: 'Fără credite',
  serviceTitle: 'Consum de credite pe serviciu',
  serviceSub: 'Credite pe zi pentru toată compania',
  serviceSubPersonal: 'Creditele dvs. pe zi',
  chat: 'Chat',
  agents: 'Agenți',
  apps: 'Aplicații',
  service: 'Serviciu',
  credits: 'Credite',
  share: 'Pondere',
  avgPerDay: 'Medie pe zi',
  total: 'Total',
  empty: 'Nu s-au folosit credite în această perioadă',
  emptyBody: 'Consumul apare aici când echipa dvs. folosește Chat, Agenți și Aplicații.',
  userTitle: 'Consum de credite pe utilizator',
  userSub: 'Credite pe zi pentru cei mai activi utilizatori ({count}) și pentru toți ceilalți',
  userSubOne: 'Credite pe zi pe serviciu pentru {name}',
  everyoneElse: 'Toți ceilalți ({count})',
  member: 'Membru',
  usedLimit: 'Folosit / limită lunară',
  limitColumn: 'Limită',
  selectedColumn: 'Selectat',
  you: 'Dvs.',
  owner: 'Proprietar',
  admin: 'Administrator',
  memberRole: 'Membru',
  pausedTag: 'Suspendat',
  usedNoLimit: 'Folosite: {used} · Fără limită',
  noLimit: 'Fără limită',
  ofLimit: '/ {limit}',
  setLimit: 'Setați limita',
  editLimit: 'Editați limita',
  showSpendFor: 'Afișați consumul pentru {name}',
  selectedShowAll: 'Selectat: {name}. Afișați din nou pe toată lumea',
  limitFor: 'Limita lunară pentru {name}',
  creditsPerMonth: 'credite/lună',
  limitHelp: 'Alegeți câte credite poate folosi {name} în fiecare lună.',
  limitInvalid: 'Introduceți un număr întreg de credite, cel puțin 1.',
  limitPausesNow:
    'Utilizatorul {name} a folosit deja {used} în această perioadă, așa că această limită îi suspendă imediat utilizarea.',
  limitHelpValid:
    'Utilizatorul {name} a folosit {used} în această perioadă. Primește o notificare la {notice}, iar utilizarea se suspendă la {limit}.',
  save: 'Salvați',
  saving: 'Se salvează…',
  removeLimit: 'Eliminați limita',
  cancel: 'Anulați',
  limitSaved: 'Limita lunară pentru {name} este de {limit} de credite.',
  limitRemoved: '{name} nu mai are limită lunară.',
  limitFailed: 'Limita nu a putut fi salvată. Încercați din nou.',
  noteLimits:
    'Un membru care își atinge limita este suspendat până la următoarea reînnoire a planului sau până când proprietarul o mărește. Primește o notificare la 80%.',
  noteAdmin:
    'Vedeți consumul tuturor pentru că sunteți proprietar sau administrator. Membrii își văd doar propriul consum.',
  notePersonal: 'Vedeți propriul consum. Proprietarii și administratorii văd consumul tuturor.',
  summaryCredits: 'Credite',
  summaryShare: 'Pondere în consumul companiei',
  summaryTop: 'Folosește cel mai mult',
  summaryTopShare: '{percent} din creditele proprii',
  summaryRoleYou: '{role} · Dvs.',
  showAllUsers: 'Afișați toți utilizatorii',
  planChanged: 'Planul dvs. a fost actualizat.',
};

const sk: Copy = {
  eyebrow: 'Využitie vo firme',
  title: 'Využitie',
  intro: 'Zdieľaný zostatok kreditov vašej firmy a ako ho využíva váš tím.',
  introPersonal: 'Zdieľaný zostatok kreditov vašej firmy a ako ho využívate vy.',
  refresh: 'Obnoviť',
  updated: 'Aktualizované o {time}',
  updatedNow: 'Práve aktualizované',
  from: 'Od',
  to: 'Do',
  chooseFirstDay: 'Vyberte prvý deň',
  chooseLastDay: 'Vyberte posledný deň',
  previousMonth: 'Predchádzajúci mesiac',
  nextMonth: 'Nasledujúci mesiac',
  last7: 'Posledných 7 dní',
  last30: 'Posledných 30 dní',
  last90: 'Posledných 90 dní',
  thisPeriod: 'Aktuálne obdobie',
  person: 'Osoba',
  allUsers: 'Všetci používatelia',
  peopleNoun: 'one:osoba|few:osoby|many:osoby|other:osôb',
  personMenu: 'Zobraziť spotrebu jednej osoby',
  loading: 'Načítava sa využitie…',
  unavailable: 'Využitie momentálne nie je k dispozícii.',
  forbidden: 'Nemáte prístup k využitiu tejto firmy.',
  tryAgain: 'Skúsiť znova',
  balanceTitle: 'Zostatok kreditov',
  creditsUnit: 'kreditov',
  paused: 'Využívanie pozastavené',
  planPerMonth: '{credits} kreditov mesačne',
  planRenews: '+{credits} kreditov {date}',
  planSwitches: 'Od {date}: {credits} kreditov mesačne',
  planEnds: 'Plán končí {date}',
  noPlan: 'Zatiaľ bez plánu',
  planPaymentFailed: 'Platba zlyhala',
  planEndedOn: 'Skončil {date}',
  planEnded: 'Skončený',
  openBilling: 'Prejsť na Fakturáciu',
  summaryTopShareSelf: '{percent} vašich kreditov',
  discountUntil: 'Zľava {percent} do {date}',
  discountOngoing: 'Zľava {percent}',
  neverExpire: 'Kredity nikdy nevypršia',
  changePlan: 'Zmeniť plán',
  upgradeNow: 'Prejsť na vyšší plán',
  choosePlan: 'Vybrať plán',
  manageOnWeb: 'Plány sa spravujú vo webovej verzii Poneva.',
  noticePlanOwner:
    'Využívanie je pozastavené, pretože firma nemá aktívny plán. Vyberte plán, aby ste mohli znova používať kredity.',
  noticePlanMember:
    'Využívanie je pozastavené, pretože firma nemá aktívny plán. Požiadajte vlastníka firmy, aby nejaký vybral.',
  noticeOutOwner:
    'Vyčerpali ste všetky kredity. Prejdite na vyšší plán alebo počkajte do {date} o {time}, keď plán pridá {credits} kreditov.',
  noticeOutMember:
    'Vaša firma vyčerpala všetky kredity. Požiadajte vlastníka firmy o vyšší plán alebo počkajte do {date} o {time}, keď plán pridá {credits} kreditov.',
  noticeOutNoRenewal: 'Vyčerpali ste všetky kredity. Vyberte plán a získajte ďalšie.',
  noticeLimit:
    'Dosiahli ste mesačný limit ({limit} kreditov). Kredity budete môcť znova používať {date} alebo skôr, ak vám vlastník firmy limit zvýši.',
  noticeLimitSoon:
    'Využili ste {used} z {limit} mesačných kreditov. Pri dosiahnutí {limit} sa využívanie pozastaví.',
  unpriced: 'Počet akcií, ktoré zatiaľ nemajú cenu a nie sú zahrnuté v týchto číslach: {count}.',
  balanceAria:
    'Zostatok kreditov od {from} do {to}. Aktuálny zostatok: {balance}. Šípkami môžete prechádzať jednotlivé dni.',
  chartAria: 'Kredity po dňoch od {from} do {to}. Šípkami môžete prechádzať jednotlivé dni.',
  flagRenewed: 'Obnovené {date}',
  flagNewPlan: 'Nový plán {date}',
  flagAdded: 'Pridané {date}',
  nextRenewal: 'obnovenie {date}',
  nextNewPlan: 'nový plán od {date}',
  today: 'Dnes, {time}',
  soFar: '{day}, zatiaľ',
  spent: 'Minuté {credits}',
  spentSoFar: 'Zatiaľ minuté {credits}',
  fromNewPlan: '+{credits} z nového plánu',
  atRenewal: '+{credits} pri obnovení',
  added: 'Pridané +{credits}',
  balanceValue: 'Zostatok: {credits}',
  creditsInTotal: 'spolu',
  outOfCredits: 'Kredity vyčerpané',
  serviceTitle: 'Spotreba kreditov podľa služby',
  serviceSub: 'Denné kredity celej firmy',
  serviceSubPersonal: 'Vaše denné kredity',
  chat: 'Chat',
  agents: 'Agenti',
  apps: 'Aplikácie',
  service: 'Služba',
  credits: 'Kredity',
  share: 'Podiel',
  avgPerDay: 'Priem. za deň',
  total: 'Spolu',
  empty: 'V tomto období sa nepoužili žiadne kredity',
  emptyBody: 'Spotreba sa tu zobrazí, keď váš tím začne používať Chat, Agentov a Aplikácie.',
  userTitle: 'Spotreba kreditov podľa používateľa',
  userSub: 'Denné kredity najaktívnejších používateľov ({count}) a všetkých ostatných',
  userSubOne: 'Denné kredity používateľa {name} podľa služby',
  everyoneElse: 'Všetci ostatní ({count})',
  member: 'Člen',
  usedLimit: 'Využité / mesačný limit',
  limitColumn: 'Limit',
  selectedColumn: 'Vybrané',
  you: 'Vy',
  owner: 'Vlastník',
  admin: 'Správca',
  memberRole: 'Člen',
  pausedTag: 'Pozastavené',
  usedNoLimit: 'Využité {used} · Bez limitu',
  noLimit: 'Bez limitu',
  ofLimit: '/ {limit}',
  setLimit: 'Nastaviť limit',
  editLimit: 'Upraviť limit',
  showSpendFor: 'Zobraziť spotrebu používateľa {name}',
  selectedShowAll: 'Vybraný používateľ {name}. Znova zobraziť všetkých',
  limitFor: 'Mesačný limit používateľa {name}',
  creditsPerMonth: 'kreditov mesačne',
  limitHelp: 'Zvoľte, koľko kreditov môže {name} každý mesiac využiť.',
  limitInvalid: 'Zadajte celé číslo, aspoň 1.',
  limitPausesNow:
    'Používateľ {name} má v tomto období využité už {used}, takže tento limit využívanie okamžite pozastaví.',
  limitHelpValid:
    'Používateľ {name} má v tomto období využité {used}. Pri {notice} príde upozornenie a pri {limit} sa využívanie pozastaví.',
  save: 'Uložiť',
  saving: 'Ukladá sa…',
  removeLimit: 'Odstrániť limit',
  cancel: 'Zrušiť',
  limitSaved: 'Mesačný limit používateľa {name}: {limit}.',
  limitRemoved: 'Používateľ {name} už nemá mesačný limit.',
  limitFailed: 'Limit sa nepodarilo uložiť. Skúste to znova.',
  noteLimits:
    'Člen, ktorý dosiahne svoj limit, je pozastavený do najbližšieho obnovenia plánu alebo kým mu vlastník limit nezvýši. Pri 80 % dostane upozornenie.',
  noteAdmin:
    'Vidíte spotrebu všetkých, pretože ste vlastník alebo správca. Členovia vidia len svoju.',
  notePersonal: 'Vidíte svoju spotrebu. Vlastníci a správcovia vidia spotrebu všetkých.',
  summaryCredits: 'Kredity',
  summaryShare: 'Podiel na spotrebe firmy',
  summaryTop: 'Najviac využíva',
  summaryTopShare: '{percent} vlastných kreditov',
  summaryRoleYou: '{role} · Vy',
  showAllUsers: 'Zobraziť všetkých používateľov',
  planChanged: 'Váš plán bol aktualizovaný.',
};

const sl: Copy = {
  eyebrow: 'Poraba podjetja',
  title: 'Poraba',
  intro: 'Skupno stanje kreditov vašega podjetja in kako ga porablja vaša ekipa.',
  introPersonal: 'Skupno stanje kreditov vašega podjetja in kako ga porabljate vi.',
  refresh: 'Osveži',
  updated: 'Posodobljeno ob {time}',
  updatedNow: 'Pravkar posodobljeno',
  from: 'Od',
  to: 'Do',
  chooseFirstDay: 'Izberite prvi dan',
  chooseLastDay: 'Izberite zadnji dan',
  previousMonth: 'Prejšnji mesec',
  nextMonth: 'Naslednji mesec',
  last7: 'Zadnjih 7 dni',
  last30: 'Zadnjih 30 dni',
  last90: 'Zadnjih 90 dni',
  thisPeriod: 'Trenutno obdobje',
  person: 'Oseba',
  allUsers: 'Vsi uporabniki',
  peopleNoun: 'one:oseba|two:osebi|few:osebe|other:oseb',
  personMenu: 'Prikaži porabo ene osebe',
  loading: 'Nalaganje porabe…',
  unavailable: 'Poraba trenutno ni na voljo.',
  forbidden: 'Nimate dostopa do porabe tega podjetja.',
  tryAgain: 'Poskusi znova',
  balanceTitle: 'Stanje kreditov',
  creditsUnit: 'kreditov',
  paused: 'Uporaba zaustavljena',
  planPerMonth: '{credits} kreditov na mesec',
  planRenews: '+{credits} kreditov {date}',
  planSwitches: 'Od {date}: {credits} kreditov na mesec',
  planEnds: 'Paket se konča {date}',
  noPlan: 'Še brez paketa',
  planPaymentFailed: 'Plačilo ni uspelo',
  planEndedOn: 'Končan {date}',
  planEnded: 'Končan',
  openBilling: 'Pojdi na Obračun',
  summaryTopShareSelf: '{percent} vaših kreditov',
  discountUntil: '{percent} popusta do {date}',
  discountOngoing: '{percent} popusta',
  neverExpire: 'Krediti nikoli ne potečejo',
  changePlan: 'Spremeni paket',
  upgradeNow: 'Nadgradi zdaj',
  choosePlan: 'Izberi paket',
  manageOnWeb: 'Pakete upravljate v spletni različici Poneva.',
  noticePlanOwner:
    'Uporaba je zaustavljena, ker podjetje nima aktivnega paketa. Izberite paket, da boste lahko spet uporabljali kredite.',
  noticePlanMember:
    'Uporaba je zaustavljena, ker podjetje nima aktivnega paketa. Prosite lastnika podjetja, naj ga izbere.',
  noticeOutOwner:
    'Porabili ste vse kredite. Nadgradite zdaj ali počakajte do {date} ob {time}, ko bo paket dodal {credits} kreditov.',
  noticeOutMember:
    'Vaše podjetje je porabilo vse kredite. Prosite lastnika podjetja za nadgradnjo ali počakajte do {date} ob {time}, ko bo paket dodal {credits} kreditov.',
  noticeOutNoRenewal: 'Porabili ste vse kredite. Izberite paket, da dobite več.',
  noticeLimit:
    'Dosegli ste mesečno omejitev ({limit} kreditov). Kredite boste lahko spet uporabljali {date} ali prej, če lastnik podjetja zviša vašo omejitev.',
  noticeLimitSoon:
    'Porabili ste {used} od {limit} mesečnih kreditov. Ko dosežete {limit}, bo uporaba zaustavljena.',
  unpriced: 'Število dejanj, ki še nimajo cene in niso vključena v te številke: {count}.',
  balanceAria:
    'Stanje kreditov od {from} do {to}. Trenutno stanje: {balance}. S puščičnimi tipkami preberite posamezne dni.',
  chartAria: 'Krediti po dnevih od {from} do {to}. S puščičnimi tipkami preberite posamezne dni.',
  flagRenewed: 'Obnovljeno {date}',
  flagNewPlan: 'Nov paket {date}',
  flagAdded: 'Dodano {date}',
  nextRenewal: 'obnovitev {date}',
  nextNewPlan: 'nov paket od {date}',
  today: 'Danes, {time}',
  soFar: '{day}, doslej',
  spent: 'Porabljeno {credits}',
  spentSoFar: 'Doslej porabljeno {credits}',
  fromNewPlan: '+{credits} iz novega paketa',
  atRenewal: '+{credits} ob obnovitvi',
  added: 'Dodano +{credits}',
  balanceValue: 'Stanje: {credits}',
  creditsInTotal: 'skupaj',
  outOfCredits: 'Ni več kreditov',
  serviceTitle: 'Poraba kreditov po storitvah',
  serviceSub: 'Dnevni krediti celotnega podjetja',
  serviceSubPersonal: 'Vaši dnevni krediti',
  chat: 'Klepet',
  agents: 'Agenti',
  apps: 'Aplikacije',
  service: 'Storitev',
  credits: 'Krediti',
  share: 'Delež',
  avgPerDay: 'Povpr. na dan',
  total: 'Skupaj',
  empty: 'V tem obdobju ni bilo porabljenih kreditov',
  emptyBody:
    'Poraba bo prikazana tukaj, ko bo vaša ekipa uporabljala Klepet, Agente in Aplikacije.',
  userTitle: 'Poraba kreditov po uporabnikih',
  userSub: 'Dnevni krediti najaktivnejših uporabnikov ({count}) in vseh drugih',
  userSubOne: 'Dnevni krediti osebe {name} po storitvah',
  everyoneElse: 'Vsi drugi ({count})',
  member: 'Član',
  usedLimit: 'Porabljeno / mesečna omejitev',
  limitColumn: 'Omejitev',
  selectedColumn: 'Izbrano',
  you: 'Vi',
  owner: 'Lastnik',
  admin: 'Skrbnik',
  memberRole: 'Član',
  pausedTag: 'Zaustavljeno',
  usedNoLimit: 'Porabljeno {used} · Brez omejitve',
  noLimit: 'Brez omejitve',
  ofLimit: '/ {limit}',
  setLimit: 'Nastavi omejitev',
  editLimit: 'Uredi omejitev',
  showSpendFor: 'Prikaži porabo osebe {name}',
  selectedShowAll: 'Izbrana oseba: {name}. Znova prikaži vse',
  limitFor: 'Mesečna omejitev osebe {name}',
  creditsPerMonth: 'kreditov na mesec',
  limitHelp: 'Izberite, koliko kreditov lahko {name} porabi vsak mesec.',
  limitInvalid: 'Vnesite celo število kreditov, vsaj 1.',
  limitPausesNow:
    'Poraba osebe {name} v tem obdobju je že {used}, zato ta omejitev takoj zaustavi uporabo.',
  limitHelpValid:
    'Poraba osebe {name} v tem obdobju je {used}. Pri {notice} prejme obvestilo, pri {limit} pa se uporaba zaustavi.',
  save: 'Shrani',
  saving: 'Shranjevanje…',
  removeLimit: 'Odstrani omejitev',
  cancel: 'Prekliči',
  limitSaved: 'Mesečna omejitev osebe {name}: {limit}.',
  limitRemoved: 'Oseba {name} nima več mesečne omejitve.',
  limitFailed: 'Omejitve ni bilo mogoče shraniti. Poskusite znova.',
  noteLimits:
    'Član, ki doseže svojo omejitev, je zaustavljen do naslednje obnovitve paketa ali dokler lastnik ne zviša omejitve. Pri 80 % prejme obvestilo.',
  noteAdmin: 'Vidite porabo vseh, ker ste lastnik ali skrbnik. Člani vidijo samo svojo.',
  notePersonal: 'Vidite svojo porabo. Lastniki in skrbniki vidijo porabo vseh.',
  summaryCredits: 'Krediti',
  summaryShare: 'Delež porabe podjetja',
  summaryTop: 'Največ uporablja',
  summaryTopShare: '{percent} lastnih kreditov',
  summaryRoleYou: '{role} · Vi',
  showAllUsers: 'Prikaži vse uporabnike',
  planChanged: 'Vaš paket je posodobljen.',
};

const es: Copy = {
  eyebrow: 'Uso de la empresa',
  title: 'Uso',
  intro: 'El saldo de créditos compartido de su empresa y cómo lo usa su equipo.',
  introPersonal: 'El saldo de créditos compartido de su empresa y cómo lo usa usted.',
  refresh: 'Actualizar',
  updated: 'Actualizado a las {time}',
  updatedNow: 'Actualizado ahora mismo',
  from: 'Desde',
  to: 'Hasta',
  chooseFirstDay: 'Elija el primer día',
  chooseLastDay: 'Elija el último día',
  previousMonth: 'Mes anterior',
  nextMonth: 'Mes siguiente',
  last7: 'Últimos 7 días',
  last30: 'Últimos 30 días',
  last90: 'Últimos 90 días',
  thisPeriod: 'Periodo actual',
  person: 'Persona',
  allUsers: 'Todos los usuarios',
  peopleNoun: 'one:persona|many:de personas|other:personas',
  personMenu: 'Mostrar el consumo de una persona',
  loading: 'Cargando el uso…',
  unavailable: 'El uso no está disponible en este momento.',
  forbidden: 'No tiene acceso al uso de esta empresa.',
  tryAgain: 'Reintentar',
  balanceTitle: 'Saldo de créditos',
  creditsUnit: 'créditos',
  paused: 'Uso en pausa',
  planPerMonth: '{credits} créditos/mes',
  planRenews: '+{credits} créditos el {date}',
  planSwitches: 'Cambia a {credits} créditos/mes el {date}',
  planEnds: 'El plan termina el {date}',
  noPlan: 'Aún sin plan',
  planPaymentFailed: 'Pago fallido',
  planEndedOn: 'Terminó el {date}',
  planEnded: 'Finalizado',
  openBilling: 'Ir a Facturación',
  summaryTopShareSelf: '{percent} de sus propios créditos',
  discountUntil: '{percent} de descuento hasta el {date}',
  discountOngoing: '{percent} de descuento',
  neverExpire: 'Los créditos no caducan nunca',
  changePlan: 'Cambiar de plan',
  upgradeNow: 'Mejorar el plan',
  choosePlan: 'Elegir un plan',
  manageOnWeb: 'Los planes se gestionan en Poneva en la web.',
  noticePlanOwner:
    'El uso está en pausa porque la empresa no tiene ningún plan activo. Elija un plan para volver a usar créditos.',
  noticePlanMember:
    'El uso está en pausa porque la empresa no tiene ningún plan activo. Pida al propietario de la empresa que elija uno.',
  noticeOutOwner:
    'Ha usado todos sus créditos. Mejore ahora su plan o espere hasta el {date} a las {time}, cuando su plan añadirá {credits} créditos.',
  noticeOutMember:
    'Su empresa ha usado todos sus créditos. Pida al propietario de la empresa que mejore el plan o espere hasta el {date} a las {time}, cuando el plan añadirá {credits} créditos.',
  noticeOutNoRenewal: 'Ha usado todos sus créditos. Elija un plan para obtener más.',
  noticeLimit:
    'Ha alcanzado su límite mensual de {limit} créditos. Podrá volver a usar créditos el {date}, o antes si el propietario de la empresa aumenta su límite.',
  noticeLimitSoon:
    'Ha usado {used} de sus {limit} créditos mensuales. Su uso se pausará al llegar a {limit}.',
  unpriced: '{count} acciones aún no tienen precio y no se incluyen en estas cifras.',
  balanceAria:
    'Saldo de créditos del {from} al {to}. Ahora {balance} créditos. Use las teclas de flecha para recorrer los días.',
  chartAria:
    'Créditos diarios del {from} al {to}. Use las teclas de flecha para recorrer los días.',
  flagRenewed: 'Renovado el {date}',
  flagNewPlan: 'Nuevo plan el {date}',
  flagAdded: 'Añadidos el {date}',
  nextRenewal: 'el {date}',
  nextNewPlan: 'nuevo plan desde el {date}',
  today: 'Hoy, {time}',
  soFar: '{day}, hasta ahora',
  spent: '{credits} gastados',
  spentSoFar: '{credits} gastados hasta ahora',
  fromNewPlan: '+{credits} del nuevo plan',
  atRenewal: '+{credits} en la renovación',
  added: '+{credits} añadidos',
  balanceValue: '{credits} créditos',
  creditsInTotal: 'créditos en total',
  outOfCredits: 'Sin créditos',
  serviceTitle: 'Consumo de créditos por servicio',
  serviceSub: 'Créditos diarios de toda la empresa',
  serviceSubPersonal: 'Sus créditos diarios',
  chat: 'Chat',
  agents: 'Agentes',
  apps: 'Apps',
  service: 'Servicio',
  credits: 'Créditos',
  share: 'Porcentaje',
  avgPerDay: 'Media diaria',
  total: 'Total',
  empty: 'No se usaron créditos en este periodo',
  emptyBody: 'El consumo aparecerá aquí cuando su equipo use Chat, Agentes y Apps.',
  userTitle: 'Consumo de créditos por usuario',
  userSub: 'Créditos diarios de los {count} usuarios con más consumo y del resto',
  userSubOne: 'Créditos diarios por servicio de {name}',
  everyoneElse: 'El resto ({count})',
  member: 'Miembro',
  usedLimit: 'Usado / límite mensual',
  limitColumn: 'Límite',
  selectedColumn: 'Seleccionado',
  you: 'Usted',
  owner: 'Propietario',
  admin: 'Administrador',
  memberRole: 'Miembro',
  pausedTag: 'En pausa',
  usedNoLimit: '{used} usados · Sin límite',
  noLimit: 'Sin límite',
  ofLimit: '/ {limit}',
  setLimit: 'Fijar límite',
  editLimit: 'Editar límite',
  showSpendFor: 'Mostrar el consumo de {name}',
  selectedShowAll: '{name}, seleccionado. Mostrar a todos de nuevo',
  limitFor: 'Límite mensual de {name}',
  creditsPerMonth: 'créditos/mes',
  limitHelp: 'Elija cuántos créditos puede usar {name} cada mes.',
  limitInvalid: 'Introduzca un número entero de créditos, 1 o más.',
  limitPausesNow:
    '{name} ya ha usado {used} en este periodo, así que este límite pausa su uso de inmediato.',
  limitHelpValid:
    '{name} ha usado {used} en este periodo. Recibirá un aviso al llegar a {notice} y su uso se pausará al llegar a {limit}.',
  save: 'Guardar',
  saving: 'Guardando…',
  removeLimit: 'Quitar límite',
  cancel: 'Cancelar',
  limitSaved: 'El límite mensual de {name} es de {limit} créditos.',
  limitRemoved: '{name} ya no tiene límite mensual.',
  limitFailed: 'No se pudo guardar el límite. Inténtelo de nuevo.',
  noteLimits:
    'Un miembro que alcanza su límite queda en pausa hasta la próxima renovación del plan o hasta que el propietario lo aumente. Recibe un aviso al 80 %.',
  noteAdmin:
    'Ve el consumo de todos porque es propietario o administrador. Los miembros solo ven el suyo.',
  notePersonal: 'Ve su propio consumo. Los propietarios y administradores ven el de todos.',
  summaryCredits: 'Créditos',
  summaryShare: 'Parte del consumo de la empresa',
  summaryTop: 'Lo que más usa',
  summaryTopShare: '{percent} de sus créditos',
  summaryRoleYou: '{role} · Usted',
  showAllUsers: 'Mostrar todos los usuarios',
  planChanged: 'Su plan se ha actualizado.',
};

export const usageCopy: NamespaceCatalog<UsageCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default usageCopy;
